// App.js
import React, { useState, useEffect, useContext, Suspense, lazy } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams
} from 'react-router-dom';
// Lazy load jsPDF - only needed when actually generating PDFs
// import jsPDF from 'jspdf'; // REMOVED - will lazy load when needed

// Keep critical components that are needed immediately (non-lazy)
import SideNavbar from './components/dashboard/SideNavbar.js';
import logoImage from './assets/images/Pulc Logos/PNG/pulc_logo_spark_gradient.png';
import {AuthContext} from './context/AuthContext';
import './styles/App.scss';
import { setupAuthInterceptor } from './utils/authInterceptor';
import { GoogleOAuthProvider } from '@react-oauth/google';
import LoadingAnecdotes from './components/shared/LoadingAnecdotes.js';
import Loading from './components/shared/Loading.js';
import CookieConsentBanner from './components/shared/CookieConsentBanner';
import { Analytics } from '@vercel/analytics/react';
import { track } from './utils/analytics';
import logger from './utils/logger';
import ErrorBoundary from './components/shared/ErrorBoundary';
import RateLimitModal from './components/shared/RateLimitModal';
import { getApiBaseUrl } from './utils/environmentConfig';
import { compressImage } from './utils/imageCompression';
import OnboardingModal from './components/onboarding/OnboardingModal';

// Lazy load all route components for code splitting
const Register = lazy(() => import('./components/auth/Register.js'));
const MultiStepRegister = lazy(() => import('./components/auth/MultiStepRegister.js'));
const VerifyEmail = lazy(() => import('./components/auth/VerifyEmail.js'));
const MainContent = lazy(() => import('./components/essayflow/MainContent.js'));
const Header = lazy(() => import('./components/dashboard/Header.js'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const AdminFeedback = lazy(() => import('./AdminFeedback.js'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword.js'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword.js'));
const AdminEssays = lazy(() => import('./AdminEssays'));
const About = lazy(() => import('./components/landing/About.js'));
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy.js'));
const RegionalFAQ = lazy(() => import('./components/support/RegionalFAQ.js'));
const TeacherHomepage = lazy(() => import('./components/landing/TeacherHomepage.js'));
const MySubmissions = lazy(() => import('./components/dashboard/MySubmissions.js'));
const StudentSubmissions = lazy(() => import('./components/dashboard/StudentSubmissions.js'));
const MyClassrooms = lazy(() => import('./components/dashboard/MyClassrooms.js'));
const MyAccount = lazy(() => import('./components/dashboard/MyAccount'));
const TeacherQuestions = lazy(() => import('./components/dashboard/TeacherQuestions'));
const ClassroomManagementPage = lazy(() => import('./components/dashboard/ClassroomManagementPage'));
const Subscription = lazy(() => import('./components/dashboard/Subscription'));
const Winback = lazy(() => import('./components/dashboard/Winback'));
const ExamPapersSubjectSelection = lazy(() => import('./components/exam-papers/ExamPapersSubjectSelection'));
const PublicExamPapersList = lazy(() => import('./components/exam-papers/PublicExamPapersList'));
const PublicExamPaperPreview = lazy(() => import('./components/exam-papers/PublicExamPaperPreview'));
const TestMathRendering = lazy(() => import('./components/math/TestMathRendering'));
const Feedback = lazy(() => import('./components/support/Feedback'));
const HowToJoinClassroom = lazy(() => import('./components/support/HowToJoinClassroom.js'));
const HowToShareEssay = lazy(() => import('./components/support/HowToShareEssay.js'));
const Help = lazy(() => import('./components/support/Help.js'));
const TestNewDesignSystem = lazy(() => import('./components/shared/TestNewDesignSystem.js'));
const ReferralTerms = lazy(() => import('./components/legal/ReferralTerms.js'));
const NotificationCenter = lazy(() => import('./components/NotificationCenter/NotificationCenter.js'));
const AboutUK = lazy(() => import('./components/landing/About_UK.js'));
const FAQ_UK = lazy(() => import('./components/support/FAQ_UK.js'));
const IeTandCs = lazy(() => import('./components/legal/ie_TandCs.js'));
const UkTandCs = lazy(() => import('./components/legal/uk_TandCs.js'));
const AuTandCs = lazy(() => import('./components/legal/au_TandCs.js'));
const TrustworthyAI = lazy(() => import('./components/legal/TrustworthyAI.js'));
const WorkWithUs = lazy(() => import('./components/legal/WorkWithUs.js'));
const GenericExamPapers = lazy(() => import('./components/exam-papers/GenericExamPapers'));
const CategorySelectionNew = lazy(() => import('./components/essayflow/CategorySelectionNew'));
const QuestionSelectNew = lazy(() => import('./components/essayflow/QuestionSelectNew'));
const UploadStepNew = lazy(() => import('./components/essayflow/UploadStepNew'));
const ConfirmAnswerStepNew = lazy(() => import('./components/essayflow/ConfirmAnswerStepNew'));
const ResultAndRevision = lazy(() => import('./components/essayflow/ResultAndRevision'));
const PendingReviewConfirmation = lazy(() => import('./components/essayflow/PendingReviewConfirmation'));
const QuizWorkflow = lazy(() => import('./components/quiz/QuizWorkflow'));
const FlashcardWorkflow = lazy(() => import('./components/flashcards/FlashcardWorkflow'));
const ComprehensionView = lazy(() => import('./components/comprehension/ComprehensionView'));
const StudentNotesWorkflow = lazy(() => import('./components/docflow/StudentNotesWorkflow'));
const TeacherDocumentManager = lazy(() => import('./components/docflow/TeacherDocumentManager'));
const LoginPage = lazy(() => import('./components/auth/LoginPage.js'));
const RegionalLandingPage = lazy(() => import('./components/landing/RegionalLandingPage.js'));
const StudentSubmissionsList = lazy(() => import('./components/comprehension/StudentSubmissionsList'));
const ComprehensionResults = lazy(() => import('./components/comprehension/ComprehensionResults'));
const TodoList = lazy(() => import('./components/dashboard/TodoList.js'));
const EssayLoader = lazy(() => import('./components/EssayLoader'));
const StudyPlans = lazy(() => import('./components/studyplan/StudyPlans'));
const TeacherClassroomSetup = lazy(() => import('./components/support/TeacherClassroomSetup.js'));
const IrishSpellcheckEditor = lazy(() => import('./components/essayflow/IrishSpellcheckEditor'));
const DevMathFeedbackPage = lazy(() => import('./components/math/DevMathFeedbackPage'));

// PDF.js will be lazy loaded in MainContent.js when needed
// Removed from top-level imports to reduce initial bundle size

async function pdfFileToImages(file, targetMaxWidth = 1920) {
  // Lazy load PDF.js only when actually processing a PDF
  const pdfjsLib = await import('pdfjs-dist');
  
  // Configure worker
  if (typeof window !== 'undefined' && pdfjsLib.GlobalWorkerOptions) {
    try {
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url);
    } catch (e) {
      logger.warn('Failed to set PDF.js worker:', e);
    }
  }
  
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const imageBlobs = [];
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1.0 });
    const scale = Math.min(2, targetMaxWidth / viewport.width);
    const scaledViewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = Math.floor(scaledViewport.width);
    canvas.height = Math.floor(scaledViewport.height);
    const renderContext = { canvasContext: context, viewport: scaledViewport };
    await page.render(renderContext).promise;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (blob) imageBlobs.push(blob);
  }
  return imageBlobs;
}

// Normalize API_BASE_URL to remove trailing slashes to prevent double-slash URLs
const normalizeUrl = (url) => (url || '').replace(/\/+$/, '');
const API_BASE_URL = normalizeUrl(process.env.REACT_APP_API_BASE_URL || getApiBaseUrl());
logger.info("API_BASE_URL:", API_BASE_URL);
logger.info("ALL ENV VARS:", {
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  REACT_APP_REGION: process.env.REACT_APP_REGION,
  NODE_ENV: process.env.NODE_ENV
});

const TEACHER_ONBOARDING_SLIDES = [
  {
    id: 'teacher-welcome',
    title: 'Welcome to Pulc – your AI marking assistant.',
    description: 'AI that speeds up marking, while keeping the teacher in control.',
    nextLabel: "Let's go →",
  },
  {
    id: 'teacher-handwritten',
    title: 'Handwritten Responses, Instantly Marked',
    videoSrc: 'https://pulcvideos.s3.eu-north-1.amazonaws.com/OnBoarding1Essay.mp4',
    nextLabel: 'Next',
  },
  {
    id: 'teacher-classroom',
    title: 'Students Submit Work Through Your Classroom',
    videoSrc: 'https://pulcvideos.s3.eu-north-1.amazonaws.com/TeachOnBoarding2SubmitClass.mp4',
    nextLabel: 'Next',
  },
  {
    id: 'teacher-feedback-control',
    title: 'Stay In Control: Edit, Reject, Or Add Feedback',
    videoSrc: 'https://pulcvideos.s3.eu-north-1.amazonaws.com/TeachOnBoarding3EditFeedback.mp4',
    nextLabel: 'Next',
  },
  {
    id: 'teacher-set-question',
    title: 'Set Questions For Your Entire Class',
    videoSrc: 'https://pulcvideos.s3.eu-north-1.amazonaws.com/TeachOnBoarding4SetQuestionMP4.mp4',
    nextLabel: 'Next',
  },
  {
    id: 'teacher-actions',
    title: 'Ready to get started?',
    description: 'You now have everything you need to start marking with Pulc.',
    nextLabel: 'Continue',
  },
];

// Add this function to print multiline text with pagination, test
function _printMultilineText(doc, textLines, x, yStart, lineHeight) {
  let y = yStart;
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  
  for (let i = 0; i < textLines.length; i++) {
    // Check if we need a new page
    if (y + lineHeight > pageHeight - margin) {
      doc.addPage();
      y = margin; // Reset y to top margin of new page
    }
    doc.text(textLines[i], x, y);
    y += lineHeight;
  }
  return y; // Return final y position
}

// Add this function to handle HTML content in PDFs
function _stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

// Function to check if it's a GCSE category
function isGCSECategory(category) {
  return category === 'GCSE_Shakespeare' || category === 'GCSE_19th' || category === 'GCSE_Poetry' || category === 'GCSE_Inspector';
}

// Function to parse feedback line by line
function _parseLineByLine(text, category) {
  // Decide which feedback codes to look for:
  let validCodes = '[PCLM]';
  if (isGCSECategory(category)) {
    validCodes = '[ALCM]';
  } else if (category === 'Aiste' || category === 'Grammar Checker' || (category && category.startsWith('Filiocht_')) || category === 'IE_Oral') {
    validCodes = '[GCS]';
  } else if (
    (category && category.startsWith('FR_')) ||
    (category && category.startsWith('ES_'))
  ) {
    validCodes = '[CL]';  // French/Spanish = Communication & Language
  } else if (category && category.startsWith('DE_')) {
    validCodes = '[CE]';  // German = Content & Expression
  }

  // We'll create a dynamic regex that checks for {X: ...}, 
  // where X is one of the valid codes for this category
  const isSingleBracket = (line) => {
    const pattern = new RegExp(`^\\{${validCodes}:`, 'i');
    return pattern.test(line.trim());
  };

  const lines = text.split('\n');
  const paragraphs = [];
  let currentParagraph = null;

  for (let rawLine of lines) {
    const lineTrim = rawLine.trim();
    if (!lineTrim) continue;

    if (isSingleBracket(lineTrim)) {
      // This line is feedback
      if (currentParagraph) {
        currentParagraph.feedback.push(rawLine);
      }
    } else {
      // Paragraph text or leftover short text
      if (lineTrim.length > 5) {
        if (currentParagraph && currentParagraph.text.trim().length > 0) {
          paragraphs.push(currentParagraph);
          currentParagraph = {
            text: rawLine,
            feedback: []
          };
        } else if (currentParagraph) {
          currentParagraph.text = rawLine;
        } else {
          currentParagraph = {
            text: rawLine,
            feedback: []
          };
        }
      } else {
        // lineTrim <= 5 => leftover short text
        if (!currentParagraph) {
          currentParagraph = { text: rawLine, feedback: [] };
        } else {
          currentParagraph.text += '\n' + rawLine;
        }
      }
    }
  }
  if (currentParagraph && currentParagraph.text.trim().length > 0) {
    paragraphs.push(currentParagraph);
  }
  
  return paragraphs;
}

// Function to strip inline annotations
function _stripInlineAnnotations(text) {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/\{\{[PCLMGSE]:.*?\}\}/g, '').replace(/\\n/g, '\n');
}

// Update pollOCRStatus to accept navigate and category
async function _pollOCRStatus(taskId, authToken, setIsLoading, setAnswer, setUsageRemaining, category, navigate) {
  const maxAttempts = 90;
  let attempts = 0;

  while (attempts < maxAttempts) {
    attempts++;
    const baseInterval = Math.min(2000 * Math.pow(1.2, Math.floor(attempts / 5)), 10000);
    const interval = baseInterval * (0.8 + Math.random() * 0.4);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/ocr-status/${taskId}`,
        {
          headers: { 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}` }
        }
      );
      const data = await response.json();

      if (attempts === 1 || attempts % 5 === 0) {
        logger.info(`OCR Status (attempt ${attempts}):`, data.state);
      }

      if (data.state === 'SUCCESS') {
        logger.info('OCR polling SUCCESS, result:', data.result);
        setAnswer(data.result.response);
        if (data.result.usageRemaining !== undefined) {
          setUsageRemaining(data.result.usageRemaining);
        }
        setIsLoading(false);
        // Use navigate to go to confirm step
        if (category) {
          logger.info('Navigating to confirm step for category:', category);
          navigate(`/essay/${category.toLowerCase()}/confirm`);
        } else {
          logger.warn('No category set, cannot navigate to confirm step.');
        }
        logger.info('Called navigate() after OCR SUCCESS');
        return;
      } else if (data.state === 'FAILURE') {
        setIsLoading(false);
        alert('OCR processing failed. Please try again.');
        return;
      }

      await new Promise(resolve => setTimeout(resolve, interval));
    } catch (error) {
      logger.error(`OCR polling error (attempt ${attempts}):`, error);
      await new Promise(resolve => setTimeout(resolve, interval * 2));
      if (attempts % 3 === 0) {
        setIsLoading(false);
        alert(`OCR status check failed after ${attempts} attempts: ${error.message}`);
        return;
      }
    }
  }
  setIsLoading(false);
  alert('OCR processing timed out');
}

// Helper to poll a single OCR batch until success and return text
async function pollOCRStatusForBatch(taskId, authToken) {
  const maxAttempts = 90;
  let attempts = 0;
  while (attempts < maxAttempts) {
    attempts++;
    const baseInterval = Math.min(2000 * Math.pow(1.2, Math.floor(attempts / 5)), 10000);
    const interval = baseInterval * (0.8 + Math.random() * 0.4);
    const resp = await fetch(`${API_BASE_URL}/api/ocr-status/${taskId}`, {
      headers: { 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}` }
    });
    const data = await resp.json().catch(() => ({}));
    if (data && data.state === 'SUCCESS') {
      return (data.result && data.result.response) ? data.result.response : '';
    }
    if (data && data.state === 'FAILURE') {
      throw new Error('OCR processing failed for a batch');
    }
    await new Promise(r => setTimeout(r, interval));
  }
  throw new Error('OCR polling timed out for a batch');
}

// Helper function to extract quiz IDs from URL query params
// Returns null if not present, ensuring fresh start when not explicitly provided
function getQuizIdsFromUrl(search) {
  const searchParams = new URLSearchParams(search);
  const quizIdParam = searchParams.get('quizId');
  const attemptIdParam = searchParams.get('attemptId');
  
  const quizId = quizIdParam ? parseInt(quizIdParam, 10) : null;
  const attemptId = attemptIdParam ? parseInt(attemptIdParam, 10) : null;
  
  // Return null if parsing failed
  return {
    quizId: (!isNaN(quizId) && quizId !== null) ? quizId : null,
    attemptId: (!isNaN(attemptId) && attemptId !== null) ? attemptId : null
  };
}

// Component to handle dynamic quiz subject routes
function QuizSubjectRoute({ API_BASE_URL, authToken, userData, onBack, currentQuizId, currentAttemptId, setCurrentQuizId, setCurrentAttemptId, subjectOverride }) {
  const { subject: subjectSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Convert URL slug back to proper subject name
  const convertSlugToSubject = (slug) => {
    if (!slug) return 'Biology'; // Default fallback
    
    // Handle special cases
    if (slug === 'legalstudies' || slug === 'legal-studies') {
      return 'Legal Studies';
    }
    
    // Convert dashes to spaces and capitalize each word
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Use override if provided (for explicit routes), otherwise convert from URL slug
  const subject = subjectOverride || convertSlugToSubject(subjectSlug);
  
  // Check if quiz IDs are explicitly in URL query params
  // Only pass quiz IDs to QuizWorkflow if they're in the URL (indicating Dashboard/shared link)
  // Otherwise, clear them to ensure fresh topic selection
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const quizIdParam = searchParams.get('quizId');
    const attemptIdParam = searchParams.get('attemptId');
    
    // If quiz IDs are not in URL, clear the state to ensure fresh start
    if (!quizIdParam && !attemptIdParam) {
      if (setCurrentQuizId) setCurrentQuizId(null);
      if (setCurrentAttemptId) setCurrentAttemptId(null);
    }
  }, [location.search, setCurrentQuizId, setCurrentAttemptId]);
  
  // Only pass quiz IDs if they're explicitly in the URL query params
  const { quizId: initialQuizId, attemptId: initialAttemptId } = getQuizIdsFromUrl(location.search);
  
  return (
    <div className="app-container">
      <Header onResetApp={onBack} />
      <QuizWorkflow
        API_BASE_URL={API_BASE_URL}
        authToken={authToken}
        userData={userData}
        subject={subject}
        onBack={() => navigate('/quiz')}
        initialQuizId={initialQuizId || null}
        initialAttemptId={initialAttemptId || null}
      />
    </div>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  // Quiet noisy info/debug logs on results flows
  const QUIET_RESULTS_LOGS = true;

  // Pull from AuthContext
  const { 
    isAuthenticated, 
    authToken, 
    storeToken, 
    storeUserData,
    clearToken: _clearToken,
    usageRemaining,
    setUsageRemaining,
    userData
  } = useContext(AuthContext);

  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imageRotations, setImageRotations] = useState({}); // index -> degrees (0/90/180/270)
  const [result, setResult] = useState(null);
  const [currentQuizId, setCurrentQuizId] = useState(null); // For quiz workflow
  const [currentAttemptId, setCurrentAttemptId] = useState(null); // For quiz workflow
  const [showSurvey, _setShowSurvey] = useState(false);
  const [surveyCompleted, _setSurveyCompleted] = useState(false);
  const [showSurveyBanner, _setShowSurveyBanner] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  // Add state from MainContent.js that needs to be shared with URL routes
  const [isRevising, setIsRevising] = useState(false);
  const [editedSections, setEditedSections] = useState({});
  const [spellCheckEnabled, setSpellCheckEnabled] = useState(false);
  const [grammarWarning, setGrammarWarning] = useState(null);
  const [grammarCheckInProgress, setGrammarCheckInProgress] = useState(false);
  const [isSpellChecking, setIsSpellChecking] = useState(false);
  const [shareWithTeacher, setShareWithTeacher] = useState(false);
  const [pendingTeacherReview, setPendingTeacherReview] = useState(false);
  const [lastSubmissionPendingReview, setLastSubmissionPendingReview] = useState(false);
  const [ignoreFadaEnabled, setIgnoreFadaEnabled] = useState(false);
  const [grammarExemption, setGrammarExemption] = useState(false);
  const [userChangedExemption, setUserChangedExemption] = useState(false);
  const [isViewingFeedback, setIsViewingFeedback] = useState(false);
  const isTeacher = userData?.role === 'teacher';

  // Clear all state when auth changes
  useEffect(() => {
    if (!isAuthenticated) {
      setStep(1);
      setCategory('');
      setQuestion('');
      setAnswer('');
      setImageFiles([]);
      setResult(null);
      setShortTaskId(null);
      setLinkifyTaskId(null);
      setRawFeedback('');
      setLinkifiedFeedback('');
      setPollingPhase('SHORT');
      setFeedbackText('');
      setFeedbackSubmitted(false);
      setFeedbackCategory('Feedback on the App Functionality');
      
      // Clear new state variables
      setIsRevising(false);
      setEditedSections({});
      setSpellCheckEnabled(false);
      setGrammarWarning(null);
      setGrammarCheckInProgress(false);
      setIsSpellChecking(false);
      setShareWithTeacher(false);
      setIgnoreFadaEnabled(false);
      setGrammarExemption(false);
      setUserChangedExemption(false);
      setIsViewingFeedback(false);
      setShowOnboarding(false);
      setOnboardingDismissed(false);
      
      // Clear persisted task IDs from localStorage
      try {
        localStorage.removeItem('currentShortTaskId');
        localStorage.removeItem('continueEssay');
      } catch (e) {
        logger.error('Failed to clear localStorage:', e);
      }
    }
  }, [isAuthenticated]);

  // For two-task chaining
  const [shortTaskId, setShortTaskId] = useState(() => {
    // Initialize from localStorage if needed for page refreshes
    try {
      return localStorage.getItem('currentShortTaskId') || null;
    } catch (e) {
      logger.error('localStorage not available:', e);
      return null;
    }
  });
  const [linkifyTaskId, setLinkifyTaskId] = useState(null);
  const [rawFeedback, setRawFeedback] = useState("");
  const [linkifiedFeedback, setLinkifiedFeedback] = useState("");
  const [pollingPhase, setPollingPhase] = useState("SHORT");

  // Feedback
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('Feedback on the App Functionality');

  // UI
  const [isLoading, setIsLoading] = useState(false);
  
  // Rate limit modal state
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [rateLimitData, setRateLimitData] = useState(null);

  // For login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Debug watchers
  useEffect(() => {
    logger.info("rawFeedback updated:", rawFeedback);
  }, [rawFeedback]);

  useEffect(() => {
    logger.info("linkifiedFeedback updated:", linkifiedFeedback);
  }, [linkifiedFeedback]);

  useEffect(() => {
    logger.info("Polling phase updated:", pollingPhase);
  }, [pollingPhase]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowOnboarding(false);
      return;
    }

    if (!userData) {
      return;
    }

    const hasSeen = Boolean(userData.has_seen_onboarding);
    if (!hasSeen && !onboardingDismissed) {
      setShowOnboarding(true);
    }

    if (hasSeen) {
      setShowOnboarding(false);
    }
  }, [isAuthenticated, userData, onboardingDismissed]);

  // Debug effect for step and category changes
  useEffect(() => {
    logger.debug(`AppContent - Step: ${step}, Category: ${category}`);
  }, [step, category]);

  // Extract quizId from URL query parameters
  // Clear quiz state when navigating to routes without quiz query params to prevent stale state
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const quizIdParam = searchParams.get('quizId');
    const attemptIdParam = searchParams.get('attemptId');
    
    if (quizIdParam) {
      const quizIdNum = parseInt(quizIdParam, 10);
      if (!isNaN(quizIdNum)) {
        setCurrentQuizId(quizIdNum);
        logger.info(`Extracted quizId from URL: ${quizIdNum}`);
      } else {
        // Invalid quizId param - clear it
        setCurrentQuizId(null);
      }
    } else {
      // No quizId in URL - clear state to ensure fresh start
      setCurrentQuizId(null);
    }
    
    if (attemptIdParam) {
      const attemptIdNum = parseInt(attemptIdParam, 10);
      if (!isNaN(attemptIdNum)) {
        setCurrentAttemptId(attemptIdNum);
        logger.info(`Extracted attemptId from URL: ${attemptIdNum}`);
      } else {
        // Invalid attemptId param - clear it
        setCurrentAttemptId(null);
      }
    } else {
      // No attemptId in URL - clear state to ensure fresh start
      setCurrentAttemptId(null);
    }
  }, [location.search]);

  // Helper to determine if we are in any quiz-related view
  const _isInQuizWorkflow = () => {
    // If a specific quiz is loaded, or if the category is Biology/Chemistry/Physics/Business/Legal Studies,
    // assume we are in a quiz-related flow where the standard steps don't apply.
    if (currentQuizId !== null || category === 'Biology' || category === 'Chemistry' || category === 'Physics' || category === 'Business' || category === 'Legal Studies') {
      return true;
    }
    return false;
  };

  // Step Navigation
  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  // Handlers for question/category
  const _handleCategorySelect = (selectedCategory) => {
    if (selectedCategory === 'Grammar Checker' || selectedCategory === 'Grammar' || 
        selectedCategory === 'FR_Grammar' || selectedCategory === 'ES_Grammar' || 
        selectedCategory === 'DE_Grammar' || category === 'GCSE_FR_Grammar' ||
        selectedCategory === 'GCSE_ES_Grammar' || selectedCategory === 'GCSE_DE_Grammar') {
      // For Grammar Checker, skip directly to step 3 (image upload/answer input)
      setCategory(selectedCategory);
      setQuestion("Grammar Check");  // Set a default question
      setStep(3);
    } else {
      // Normal flow for other categories
      setCategory(selectedCategory);
      setStep(2);
    }
  };

  const handleQuestionSubmit = () => {
    if (!question.trim()) return alert('Please enter a question.');
    nextStep();
  };

  const handleAnswerChange = (e) => setAnswer(e.target.value);
  const handleImageUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...newFiles]);
  };
  const handleRemoveImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Submit images
  const handleSubmitAnswer = async () => {
    console.log('[DEBUG] handleSubmitAnswer called. answer:', answer, 'imageFiles:', imageFiles);
    if (!(answer || '').trim() && imageFiles.length === 0) {
      console.log('[DEBUG] No answer or images provided.');
      return alert('Please provide an answer or upload an image.');
    }

    setIsLoading(true);
    try {
      if (imageFiles.length > 0) {
        // Apply rotation to original files first
        const rotatedOriginals = [];
        for (let i = 0; i < imageFiles.length; i++) {
          const f = imageFiles[i];
          const rotationDegrees = imageRotations[i] || 0;
          const rotatedFile = await rotateImageFile(f, rotationDegrees);
          rotatedOriginals.push(rotatedFile);
        }

        // Expand PDFs to page images, then compress all items adaptively
        const expandedFiles = [];
        for (const f of rotatedOriginals) {
          if (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) {
            const blobs = await pdfFileToImages(f);
            let pageIndex = 0;
            for (const blob of blobs) {
              const pageFile = new File([blob], `${f.name.replace(/\.[^/.]+$/, '')}_page_${++pageIndex}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
              expandedFiles.push(pageFile);
            }
          } else {
            expandedFiles.push(f);
          }
        }

        const totalBudgetMB = 14;
        const perFileTargetMB = Math.min(0.4, Math.max(0.15, totalBudgetMB / Math.max(expandedFiles.length, 1)));

        // Auto-batch into chunks of up to 12 pages
        const BATCH_SIZE = 12;
        const batches = [];
        for (let i = 0; i < expandedFiles.length; i += BATCH_SIZE) {
          batches.push(expandedFiles.slice(i, i + BATCH_SIZE));
        }

        let mergedText = '';
        for (let b = 0; b < batches.length; b++) {
          const batch = batches[b];
          const formData = new FormData();
          
          // Add category to formData if available
          if (category && category.trim() && category.trim() !== 'None') {
            formData.append('category', category.trim());
            console.log('[DEBUG] Adding category to OCR request:', category.trim());
          } else {
            console.warn('[DEBUG] Category missing or invalid for OCR request:', category);
          }
          
          for (let i = 0; i < batch.length; i++) {
            const original = batch[i];
            const compressed = original.type.startsWith('image/')
              ? await compressImage(original, perFileTargetMB)
              : original;
            const ts = Date.now();
            const rnd = Math.random().toString(36).substring(2, 10);
            const ext = (compressed.name && compressed.name.includes('.')) ? compressed.name.split('.').pop() : (compressed.type === 'image/jpeg' ? 'jpg' : 'png');
            const uniqueName = `page_${i+1}_${ts}_${rnd}.${ext}`;
            const toUpload = new File([compressed], uniqueName, { type: compressed.type || original.type, lastModified: ts });
            formData.append('images', toUpload);
          }

          console.log('[DEBUG] Submitting OCR batch', b + 1, 'of', batches.length, `${API_BASE_URL}/api/ocr-images`);
          const imgResponse = await fetch(`${API_BASE_URL}/api/ocr-images`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${authToken}` },
            body: formData
          });
          const imgData = await imgResponse.json().catch(() => ({}));

          let batchText = '';
          if (imgResponse.status === 202 && imgData.task_id) {
            // Poll this batch and append
            batchText = await new Promise(async (resolve, reject) => {
              try {
                const res = await pollOCRStatusForBatch(imgData.task_id, authToken);
                resolve(res || '');
              } catch (e) {
                reject(e);
              }
            });
          } else if (imgData.response) {
            batchText = imgData.response || '';
          } else {
            setIsLoading(false);
            alert('Unexpected OCR response for a batch. Please try again.');
            return;
          }

          // Merge batch text (removed automatic save-as-document for essay flow)
          if (batchText) {
            mergedText += (mergedText ? '\n\n' : '') + batchText;
          }
        }

        if (mergedText) {
          setAnswer(mergedText);
        }
        setIsLoading(false);
        if (category) {
          navigate(`/essay/${category.toLowerCase()}/confirm`);
        }
        return;
      }
      setIsLoading(false);
      nextStep();
    } catch (error) {
      console.error('[DEBUG] handleSubmitAnswer error:', error);
      alert('There was a problem submitting your answer. Please try again.');
      setIsLoading(false);
    }
  };

  // Helper function to map categories to subjects
  const getCategorySubject = (category) => {
    // Map category to subject
    if (category === 'Shakespeare' || category === 'Poetry' || 
        category === 'Comparative' || category === 'Composition' ||
        category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical' ||
        category === 'English_HSC_Textual' || category === 'English_HSC_Critical' || category === 'English_HSC_Texts' || 
        category === 'English_HSC_Craft' || category === 'English_HSC_CraftA' || category === 'English_HSC_CraftB' ||
        category === 'VCE_English_SectionA' || category === 'VCE_English_SectionB' || category === 'VCE_English_SectionC' ||
        (category && category.startsWith('GCSE_'))) {
      return 'English';
    } else if (category === 'Aiste' || category === 'Grammar Checker' || 
              category === 'IE_Oral' || (category && category.startsWith('Filiocht'))) {
      return 'Irish';
    } else if (category && category.startsWith('FR_')) {
      return 'French';
    } else if (category && category.startsWith('ES_')) {
      return 'Spanish';
    } else if (category && category.startsWith('DE_')) {
      return 'German';
    } else if (category && category.startsWith('MATH_')) {
      return 'Maths';
    } else if (category === 'Biology' || (category && category.startsWith('Biology_'))) {
      return 'Biology';
    } else if (category === 'Chemistry' || (category && category.startsWith('Chemistry_'))) {
      return 'Chemistry';
    } else if (category === 'Physics' || (category && category.startsWith('Physics_'))) {
      return 'Physics';
    } else if (category === 'Business' || category === 'Business Studies' || (category && category.startsWith('Business_'))) {
      return 'Business';
    } else if (category === 'Legal Studies' || category === 'LegalStudies' || (category && category.startsWith('LegalStudies_'))) {
      return 'Legal Studies';
    } else if (category === 'Psychology' || (category && category.startsWith('Psychology_'))) {
      return 'Psychology';
    } else if (category && category.startsWith('GEO_')) {
      return 'Geography';
    } else {
      return 'English'; // Default to English
    }
  };

  // Confirm Answer -> triggers short & linkify tasks
  const handleConfirmAnswer = async (feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, subject, pendingTeacherReview = false) => {
    if (!(answer || '').trim()) {
      return alert('Please provide an answer.');
    }
    setIsLoading(true);
    
    try {
      logger.info("Making API request to:", `${API_BASE_URL}/api/results`);

      const response = await (await import('./utils/api')).apiFetch(`${API_BASE_URL}/api/results`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question,
          answer,
          category,
          feedbackType,
          visible_to_teacher: shareWithTeacher,
          ignore_fada: ignoreFadaEnabled,
          grammar_exemption: grammarExemption,
          subject: subject || getCategorySubject(category), // Use provided subject or derive from category
          pending_teacher_review: pendingTeacherReview
        })
      });

      logger.info("API Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Results Error:', response.status, errorText);
        throw new Error(`Error triggering tasks: ${errorText}`);
      }

      const data = await response.json();
      logger.debug("API Response data:", data);
      
      // Update usage remaining if it's in the response
      if (data.usageRemaining !== undefined) {
        logger.info("Updating usage remaining to:", data.usageRemaining);
        setUsageRemaining(data.usageRemaining);
      }

      // Check if this is a pending teacher review submission
      if (data.pending_teacher_review) {
        logger.info("Essay submitted for teacher review - showing confirmation");
        setIsLoading(false);
        setLastSubmissionPendingReview(true);
        setStep(6); // Move to a new confirmation step
        return;
      }

      // Only check shortTaskId now:
      if (!data.shortTaskId) {
        logger.error("Missing shortTaskId in response:", data);
        throw new Error('Server response missing required shortTaskId');
      }

      setShortTaskId(data.shortTaskId);
      setLinkifyTaskId(data.linkifyTaskId);
      setDbTaskId(data.dbTaskId);
      setPollingPhase("SHORT");
      
      // Set the essay ID if provided for chat context
      if (data.essay_id) {
        setEssayId(data.essay_id);
      }

      // Start polling with both IDs
      if (data.linkifyTaskId) {
        pollShortTask(data.shortTaskId, data.linkifyTaskId);
      } else {
        logger.info("Skipping linkify polling for Irish category.");
        // Still do short-task polling:
        pollShortTask(data.shortTaskId, null);
      }
    } catch (error) {
      logger.error("handleConfirmAnswer error:", error);
      
      // Try to parse error as JSON to get a better message
      try {
        const errorText = error.message.replace('Error triggering tasks: ', '');
        const errorData = JSON.parse(errorText);
        
        // Check if this is a rate limit error
        if (errorData.error === 'feature_limit_reached') {
          setRateLimitData({
            feature: errorData.usage?.feature || 'essay',
            usage: errorData.usage,
            nextReset: errorData.next_reset
          });
          setShowRateLimitModal(true);
          setIsLoading(false);
          return;
        }
        
        // Check if this is a missing category error
        if (errorData.error === 'missing_category' || errorData.action_required === 'restart_flow') {
          alert(errorData.message || 'Please start from the beginning and select your essay type.');
          // Reset to beginning
          handleResetApp();
          setIsLoading(false);
          return;
        }
        
        // Other structured errors - show the message field
        if (errorData.message) {
          alert(errorData.message);
          setIsLoading(false);
          return;
        }
      } catch (parseError) {
        // Not JSON or parsing failed - continue to generic error
        logger.error('Failed to parse error response:', parseError);
      }
      
      // For other errors, show the generic alert
      alert('There was a problem sending your request: ' + error.message);
      setIsLoading(false);
    }
  };

  // Poll the short task
  const pollShortTask = async (shortId, linkifyId) => {
    logger.info("pollShortTask called with shortId:", shortId, "linkifyId:", linkifyId);
    
    // Track attempt number and use exponential backoff
    if (!window.pollAttempts) {
      window.pollAttempts = { [shortId]: 0 };
    } else if (!window.pollAttempts[shortId]) {
      window.pollAttempts[shortId] = 0;
    }
    
    window.pollAttempts[shortId]++;
    const currentAttempt = window.pollAttempts[shortId];
    
    // Calculate backoff interval - starts at 2s, caps at 8s
    // Base interval doubles every 5 attempts
    const baseInterval = Math.min(2000 * Math.pow(2, Math.floor(currentAttempt / 5)), 8000);
    // Add jitter to avoid thundering herd problem (+/- 20%)
    const jitter = baseInterval * (0.8 + Math.random() * 0.4);
    
    try {
      const statusRes = await (await import('./utils/api')).apiFetch(`${API_BASE_URL}/api/results/status/${shortId}`, {
        headers: { 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}` }
      });
      const statusData = await statusRes.json();
      
      // Only log full details periodically to reduce noise
      if (currentAttempt === 1 || currentAttempt % 5 === 0) {
        logger.info("Short task status response:", statusData);
      } else {
        logger.debug(`Short task ${shortId} status: ${statusData.state} (attempt ${currentAttempt})`);
      }

      if (statusData.state === "PENDING" || statusData.state === "STARTED") {
        setTimeout(() => pollShortTask(shortId, linkifyId), jitter);
      } else if (statusData.state === "SUCCESS") {
        logger.info("Short task succeeded:", statusData.result);

        // Update usage remaining if present
        if (statusData.usageRemaining !== undefined) {
          logger.info("Updating usage remaining from short task to:", statusData.usageRemaining);
          setUsageRemaining(statusData.usageRemaining);
        }

        if (statusData.result?.raw_feedback) {
          setRawFeedback(statusData.result.raw_feedback);
          setResult(statusData.result.raw_feedback);
        }

        setIsLoading(false);
        setStep(5);

        // Start linkify polling
        if (linkifyId) {
          startLinkifyPolling(linkifyId);
        } else {
          logger.info("Skipping linkify polling for Irish category.");
        }
        
        // Reset poll attempts for this task
        if (window.pollAttempts && window.pollAttempts[shortId]) {
          delete window.pollAttempts[shortId];
        }
      } else if (statusData.state === "FAILURE") {
        logger.error(`Short task failed: ${statusData.error}`);
        setIsLoading(false);
        alert("There was a problem processing your short task.");
        
        // Reset poll attempts for this task
        if (window.pollAttempts && window.pollAttempts[shortId]) {
          delete window.pollAttempts[shortId];
        }
      }
    } catch (err) {
      logger.error("Error polling short task:", err);
      setTimeout(() => pollShortTask(shortId, linkifyId), jitter);
    }
  };

  // Add new continuous polling function for linkify task
  const startLinkifyPolling = (linkifyId) => {
    logger.info("startLinkifyPolling initiated with linkifyId:", linkifyId);
    
    // Setup polling with exponential backoff
    if (!window.pollAttempts) {
      window.pollAttempts = {};
    }
    window.pollAttempts[linkifyId] = 0;
    
    const pollWithBackoff = async () => {
      window.pollAttempts[linkifyId]++;
      const currentAttempt = window.pollAttempts[linkifyId];
      
      // Calculate backoff interval - starts at 2s, caps at 10s
      const baseInterval = Math.min(2000 * Math.pow(2, Math.floor(currentAttempt / 5)), 10000);
      const jitter = baseInterval * (0.8 + Math.random() * 0.4);
      
      try {
        const statusRes = await (await import('./utils/api')).apiFetch(`${API_BASE_URL}/api/results/status/${linkifyId}`, {
          headers: { 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}` }
        });
        const statusData = await statusRes.json();
        
        // Only log details periodically to reduce noise
        if (currentAttempt === 1 || currentAttempt % 5 === 0) {
          logger.info("Linkify polling response:", statusData);
        } else {
          logger.debug(`Linkify task ${linkifyId} status: ${statusData.state} (attempt ${currentAttempt})`);
        }

        // Update usage remaining if present
        if (statusData.usageRemaining !== undefined) {
          logger.info("Updating usage remaining from linkify task to:", statusData.usageRemaining);
          setUsageRemaining(statusData.usageRemaining);
        }

        if (statusData.state === "SUCCESS" && statusData.result?.linkified_feedback) {
          logger.info("Linkify task succeeded with feedback");
          setLinkifiedFeedback(statusData.result.linkified_feedback);
          
          // Check if linkified_feedback is a JSON string and parse it
          let feedbackToSet = statusData.result.linkified_feedback;
          if (typeof feedbackToSet === 'string') {
            try {
              // Check if it's a JSON string (starts with { or [)
              const trimmed = feedbackToSet.trim();
              if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
                  (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                feedbackToSet = JSON.parse(feedbackToSet);
                logger.info("Parsed JSON feedback:", feedbackToSet);
              }
            } catch (e) {
              logger.error("Failed to parse linkified_feedback as JSON:", e);
              // Keep it as string if parsing fails
            }
          }
          
          setResult(feedbackToSet);
          setPollingPhase("DONE");
          
          // Reset poll attempts for this task
          if (window.pollAttempts && window.pollAttempts[linkifyId]) {
            delete window.pollAttempts[linkifyId];
          }
        } else if (statusData.state === "FAILURE") {
          logger.error("Linkify task failed:", statusData.error);
          alert("There was a problem linkifying your text.");
          
          // Reset poll attempts for this task
          if (window.pollAttempts && window.pollAttempts[linkifyId]) {
            delete window.pollAttempts[linkifyId];
          }
        } else {
          // Schedule next poll with backoff
          setTimeout(pollWithBackoff, jitter);
        }
      } catch (err) {
        logger.error("Error in linkify polling:", err);
        
        // Only retry for a reasonable number of times on error
        if (currentAttempt < 30) {
          setTimeout(pollWithBackoff, jitter);
        } else {
          logger.error("Giving up on linkify polling after too many errors");
          if (window.pollAttempts && window.pollAttempts[linkifyId]) {
            delete window.pollAttempts[linkifyId];
          }
        }
      }
    };
    
    // Start polling
    pollWithBackoff();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      logger.info("Login request URL:", `${API_BASE_URL}/api/login`);
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          identifier: username,
          password: password
        })
      });

      const data = await response.json();

      if (response.status === 403 && data.needsVerification) {
        setShowResendVerification(true);
        setUserEmail(data.email);
        setError("Your email is not verified. Click below to resend verification email.");
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      const token = data.token;
      storeToken(token);
      setUsageRemaining(data.usageRemaining);
      setError(null);
      setIsLoggedIn(true);
    } catch (error) {
      setError(error.message);
      logger.error('Login error:', error);
    }
  };
  // Rotate an image File by degrees and return a new File (JPEG)
  const rotateImageFile = async (file, degrees) => {
    try {
      if (!degrees || degrees % 360 === 0) return file;
      const img = new Image();
      const url = URL.createObjectURL(file);
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const radians = (degrees * Math.PI) / 180;
      const swap = degrees % 180 !== 0;
      canvas.width = swap ? img.height : img.width;
      canvas.height = swap ? img.width : img.height;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      URL.revokeObjectURL(url);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) return file;
      const rotatedName = file.name.replace(/(\.[^.]+)?$/, `_rot${degrees}$1`);
      return new File([blob], rotatedName, { type: 'image/jpeg', lastModified: Date.now() });
    } catch (e) {
      console.error('Rotation failed, using original file:', e);
      return file;
    }
  };

  // Rotate image at index by degrees
  const rotateImage = (index, degrees) => {
    console.log(`Rotating image ${index} by ${degrees} degrees`);
    setImageRotations(prev => {
      const currentRotation = prev[index] || 0;
      const newRotation = (currentRotation + degrees + 360) % 360;
      console.log(`Image ${index}: ${currentRotation}° → ${newRotation}°`);
      return {
        ...prev,
        [index]: newRotation
      };
    });
  };

  // Reset
  const handleResetApp = () => {
    setStep(1);
    setCategory('');
    setQuestion('');
    setAnswer('');
    setImageFiles([]);
    setImageRotations({});
    setResult(null);
    setRawFeedback("");
    setLinkifiedFeedback("");
    setSelectedBranch(null);
    setFeedbackSubmitted(false);
    setFeedbackText('');
    setFeedbackCategory('Feedback on the App Functionality');
    
    // Reset new state variables
    setIsRevising(false);
    setEditedSections({});
    setSpellCheckEnabled(false);
    setGrammarWarning(null);
    setGrammarCheckInProgress(false);
    setIsSpellChecking(false);
    setShareWithTeacher(false);
    setIgnoreFadaEnabled(false);
    setGrammarExemption(false);
    setUserChangedExemption(false);
    setIsViewingFeedback(false);
    
    // Navigate to home page
    navigate('/');
    
    // Clear quiz-related state
    setCurrentQuizId(null);
    setCurrentAttemptId(null);
    
    // Clear persisted task IDs
    try {
      localStorage.removeItem('currentShortTaskId');
      localStorage.removeItem('continueEssay');
    } catch (e) {
      logger.error('Failed to clear localStorage during reset:', e);
    }
  };

  const persistOnboardingPreference = async () => {
    if (!authToken) {
      return;
    }

    const authHeader = authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/update-preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({ has_seen_onboarding: true })
      });

      if (!response.ok) {
        logger.warn('Persist onboarding preference returned non-OK status:', response.status);
      }
    } catch (error) {
      logger.error('Failed to persist onboarding completion:', error);
    }
  };

  const completeOnboarding = async () => {
    setShowOnboarding(false);

    if (onboardingDismissed) {
      return;
    }

    setOnboardingDismissed(true);

    if (userData && !userData.has_seen_onboarding) {
      const updatedUserData = {
        ...userData,
        has_seen_onboarding: true
      };
      storeUserData(updatedUserData);
    }

    await persistOnboardingPreference();
  };

  const handleOnboardingAction = (action) => {
    const studentRouteMap = {
      'mark-essay': '/essay/subject',
      'solve-maths': '/math',
      'create-quiz': '/notes',
      'explore': '/',
    };

    const teacherRouteMap = {
      'review-submissions': '/student-submissions',
      'set-class-question': '/essay/subject',
      'manage-classroom': '/classroom-management',
      'explore-dashboard': '/dashboard',
    };

    const routeMap = isTeacher ? teacherRouteMap : studentRouteMap;
    const targetPath = routeMap[action];
    if (targetPath) {
      navigate(targetPath);
    }
  };

  // Save PDF
  const handleSavePDF = () => {
    if (!result) return;
    
    try {
      // Find the content container in the DOM
      const contentElement = document.querySelector('.content-wrapper');
      if (!contentElement) {
        alert("Couldn't find the content to save. Please try again.");
        return;
      }
      
      // Create a clone of the element to not affect what's displayed
      const clone = contentElement.cloneNode(true);
      
      // Remove elements we don't want in the PDF (buttons, feedback form, etc.)
      const buttonsContainer = clone.querySelector('.action-buttons-container');
      if (buttonsContainer) buttonsContainer.remove();
      
      const feedbackSection = clone.querySelector('.feedback-section');
      if (feedbackSection) feedbackSection.remove();
      
      const reviseButton = clone.querySelector('.result-revise-button-container');
      if (reviseButton) reviseButton.remove();
      
      const usageRemaining = clone.querySelector('.usage-remaining');
      if (usageRemaining) usageRemaining.remove();
      
      // Set up a temporary container for the clone - using a wider width
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '1000px'; // Wider width to match screen view
      document.body.appendChild(tempContainer);
      tempContainer.appendChild(clone);
      
      // Apply necessary styling to make sure it looks good in the PDF
      const tempStyle = document.createElement('style');
      tempStyle.textContent = `
        .content-wrapper {
          background-color: white;
          padding: 20px;
          font-family: Arial, sans-serif;
          color: black;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }
        .rating-buttons, button {
          display: none !important;
        }
        .feedback-item {
          page-break-inside: avoid;
          width: 100%;
        }
        .paragraph-container {
          page-break-inside: avoid;
          margin-bottom: 15px;
          width: 100%;
          display: block !important;
        }
        .paragraph-text {
          width: 100% !important;
          max-width: none !important;
          padding-right: 20px;
          font-size: 14px;
          line-height: 1.5;
        }
        .feedback-column {
          width: 100% !important;
          max-width: none !important;
          margin-top: 10px;
          padding-left: 20px;
          border-left: 1px solid #eee;
        }
        .analysis-container {
          width: 100% !important;
          max-width: none !important;
        }
        .feedback-columns {
          width: 100% !important;
          max-width: none !important;
          display: block !important;
          column-count: 1 !important;
          columns: 1 !important;
        }
        .question-box {
          width: 100% !important;
          max-width: none !important;
          margin-bottom: 20px;
        }
        /* Make text larger and more readable */
        p, div {
          font-size: 14px;
          line-height: 1.5;
        }
        /* Force reset any column or grid layouts */
        [class*="col-"], .row, .container, .container-fluid {
          width: 100% !important;
          max-width: none !important;
          flex: none !important;
          display: block !important;
        }
        /* Make all text containers take full width */
        .paragraph-text > div, .feedback-item > div {
          width: 100% !important;
          max-width: none !important;
        }
      `;
      tempContainer.appendChild(tempStyle);
      
      // Break the two-column layout if it exists
      const paragraphContainers = clone.querySelectorAll('.paragraph-container');
      paragraphContainers.forEach(container => {
        // Force paragraph containers to be block elements
        container.style.display = 'block';
        container.style.width = '100%';
        
        // Make paragraph text full width
        const paragraphText = container.querySelector('.paragraph-text');
        if (paragraphText) {
          paragraphText.style.width = '70%';
          paragraphText.style.maxWidth = 'none';
          paragraphText.style.fontSize = '14px';
          paragraphText.style.lineHeight = '1.5';
        }
        
        // Style feedback column
        const feedbackColumn = container.querySelector('.feedback-column');
        if (feedbackColumn) {
          feedbackColumn.style.width = '30%';
          feedbackColumn.style.maxWidth = 'none';
          feedbackColumn.style.marginTop = '10px';
          feedbackColumn.style.paddingLeft = '20px';
          feedbackColumn.style.borderLeft = '1px solid #eee';
        }
      });
      
      // Ensure question box is full width
      const questionBox = clone.querySelector('.question-box');
      if (questionBox) {
        questionBox.style.width = '100%';
        questionBox.style.maxWidth = 'none';
      }
      
      // Add page heading and footer manually, since we're removing the other UI
      const header = document.createElement('h1');
      header.style.textAlign = 'center';
      header.style.fontSize = '22px';
      header.style.fontFamily = 'Arial, sans-serif';
      header.style.marginBottom = '25px';
      header.textContent = 'Your Essay Analysis';
      clone.insertBefore(header, clone.firstChild);
      
      // Remove the logo in the HTML content since we'll add it programmatically to each PDF page
      // This prevents duplication on the first page
      
      const footer = document.createElement('div');
      footer.style.textAlign = 'center';
      footer.style.fontSize = '10px';
      footer.style.color = '#777';
      footer.style.marginTop = '30px';
      footer.style.paddingTop = '10px';
      footer.style.borderTop = '1px solid #eee';
      footer.style.fontFamily = 'Arial, sans-serif';
      footer.textContent = `Generated by Pulc on ${new Date().toLocaleDateString()}`;
      clone.appendChild(footer);
      clone.style.paddingBottom = "50px";
      // Import html2canvas
      import('html2canvas').then(async (html2canvas) => {
        // First, give the browser a moment to render with the new styles
        setTimeout(async () => {
          try {
            const canvas = await html2canvas.default(clone, {
              scale: 1.5, // Balanced scale for quality and file size
              useCORS: true,
              logging: false,
              allowTaint: true,
              backgroundColor: "white",
              width: tempContainer.offsetWidth,
              height: tempContainer.scrollHeight,
              windowWidth: 1000 // Match container width
            });
            
            // 1) Lazy load jsPDF and set up
            const { default: jsPDF } = await import('jspdf');
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'mm',
              format: 'a4'
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // 2) Decide margins (in mm)
            const marginX = 10; // Add horizontal margin
            const marginY = 10;
            const usableWidth = pageWidth - (2 * marginX);
            const usableHeight = pageHeight - (2 * marginY);

            // 3) Calculate how many pixels fit on one page at our target dimensions
            const ratio = canvas.width / usableWidth;
            const pageHeightInPx = usableHeight * ratio;
            
            // 4) Calculate how many pages we need
            const pagesCount = Math.ceil(canvas.height / pageHeightInPx);
            
            // 5) For each page, create a new canvas with just that portion
            for (let i = 0; i < pagesCount; i++) {
              // If not the first page, add a new page to the PDF
              if (i > 0) {
                pdf.addPage();
              }
              
              // Create a new canvas for this page section
              const pageCanvas = document.createElement('canvas');
              const pageCtx = pageCanvas.getContext('2d');
              
              // Set dimensions for this page's canvas
              pageCanvas.width = canvas.width;
              
              // Calculate the height for this page (might be less for the last page)
              const sourceY = i * pageHeightInPx;
              const remainingHeight = canvas.height - sourceY;
              const pageCanvasHeight = Math.min(pageHeightInPx, remainingHeight);
              pageCanvas.height = pageCanvasHeight;
              
              // Draw the appropriate portion of the original canvas onto this one
              pageCtx.drawImage(
                canvas, 
                0, sourceY,               // Start coordinates in source
                canvas.width, pageCanvasHeight,  // Dimensions in source
                0, 0,                     // Start coordinates in destination
                canvas.width, pageCanvasHeight   // Dimensions in destination
              );
              
              // Convert this page canvas to an image for the PDF
              const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
              
              // Add this page image to the PDF
              pdf.addImage(
                pageImgData, 
                'JPEG', 
                marginX, 
                marginY, 
                usableWidth, 
                (pageCanvasHeight / canvas.width) * usableWidth
              );
              
              // Add logo to every page
              if (i === 0) {
                // Create an Image object for the logo
                const logoElement = new Image();
                // Ensure CORS-safe for canvas operations (bundled assets should be fine, but safe to set)
                logoElement.crossOrigin = 'anonymous';
                logoElement.src = logoImage;

                // Add the logo once it's loaded
                logoElement.onload = () => {
                  // Calculate logo dimensions while maintaining aspect ratio
                  const logoWidth = 40; // in mm (doubled for ~4x area)
                  const logoHeight = (logoWidth / logoElement.width) * logoElement.height;

                  // Draw the logo to an offscreen canvas and convert to JPEG data URL
                  const logoCanvas = document.createElement('canvas');
                  logoCanvas.width = logoElement.width;
                  logoCanvas.height = logoElement.height;
                  const logoCtx = logoCanvas.getContext('2d', { willReadFrequently: true });
                  if (logoCtx) {
                    logoCtx.drawImage(logoElement, 0, 0);
                  }
                  // Export as PNG to preserve transparency (avoid black background)
                  const logoDataUrl = logoCanvas.toDataURL('image/png');

                  // Add logo to the top right of each page using JPEG data
                  for (let j = 1; j <= pagesCount; j++) {
                    pdf.setPage(j);
                    pdf.addImage(
                      logoDataUrl,
                      'PNG',
                      pageWidth - marginX - logoWidth, // Position from right
                      marginY, // Position from top
                      logoWidth, // Width in mm
                      logoHeight // Height in mm
                    );
                  }

                  // -- Page numbering logic -- //
                  const totalPages = pdf.internal.getNumberOfPages();
                  for (let j = 1; j <= totalPages; j++) {
                    pdf.setPage(j);
                    pdf.setFontSize(8);
                    pdf.setTextColor(100);
                    pdf.text(
                      `Page ${j} of ${totalPages}`,
                      pageWidth / 2,
                      pageHeight - 5,
                      { align: 'center' }
                    );
                  }

                  // Save PDF after logo is added to all pages
                  pdf.save('essay_analysis.pdf');
                };
              }
            }

          } catch (err) {
            logger.error('Error in HTML2Canvas:', err);
            alert('There was a problem generating the PDF. Please try again.');
          } finally {
            // Clean up
            document.body.removeChild(tempContainer);
          }
        }, 100); // small delay to let styles apply
      }).catch(error => {
        logger.error('Error loading html2canvas:', error);
        alert('There was a problem generating the PDF. Please try again.');
        document.body.removeChild(tempContainer);
      });
    } catch (error) {
      logger.error('Error generating PDF:', error);
      alert('There was a problem generating the PDF. Please try again.');
    }
  };

  // Feedback Submit
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    logger.info("Submitting feedback:", {  // Debug log
      feedbackText,
      feedbackCategory,
      question,
      answer,
      result
    });
    
    if (!feedbackText.trim()) {
      alert("Please enter feedback before submitting.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
        },
        body: JSON.stringify({
          feedback: feedbackText,        // user_feedback in backend
          category: feedbackCategory,    // category in backend
          question: question,            // question_text in backend
          answer: answer,                // answer_text in backend
          result: result                 // app_feedback in backend
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Feedback Error:', response.status, errorText);
        logger.error('Request details:', {  // Debug log
          feedbackText,
          feedbackCategory,
          question,
          answer,
          result,
          status: response.status
        });
        throw new Error('Error submitting feedback');
      }

      const data = await response.json();
      alert(data.message);
      setFeedbackSubmitted(true);
    } catch (error) {
      logger.error(error);
      alert("Failed to submit feedback. Please try again.");
    }
  };

  // Get username from token when auth changes
  useEffect(() => {
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        setUsername(payload.sub);
      } catch (e) {
        logger.error('Error decoding token:', e);
      }
    }
  }, [authToken]);

  // Reset to step 1 when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      setStep(1);
      setSelectedBranch(null);
    }
  }, [isAuthenticated]);

  // Add this function to handle image files
  const handleImageFiles = (files) => {
    setImageFiles(files);
  };

  // Set up the auth interceptor when the app starts
  setupAuthInterceptor();

  const handleResendVerification = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userEmail })
      });
      alert("Verification email sent! Please check your inbox.");
      setShowResendVerification(false);
    } catch (error) {
      alert("Failed to resend verification email");
    }
  };

  // Move the continueEssay check to a separate function
  const _checkForContinuedEssay = () => {
    const continueEssay = localStorage.getItem('continueEssay');
    if (continueEssay) {
      const essayData = JSON.parse(continueEssay);
      setQuestion(essayData.question);
      setAnswer(essayData.answer);
      setCategory(essayData.category || 'Shakespeare');
      setStep(4); // Always go to step 4
      localStorage.removeItem('continueEssay');
      return true;
    }
    return false;
  };

  const _handleGoogleSuccess = async (credentialResponse) => {
    try {
        logger.info('Attempting Google login with credential:', credentialResponse);
        logger.info('API URL:', `${API_BASE_URL}/api/google-login`);
        
        const response = await fetch(`${API_BASE_URL}/api/google-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                credential: credentialResponse.credential
            })
        });

        logger.info('Response received:', response);
        const data = await response.json();
        logger.info('Response data:', data);

        if (!response.ok) {
            throw new Error(data.error || 'Google login failed');
        }

        storeToken(data.token);
        setUsageRemaining(data.usageRemaining);
        setError(null);
        setIsLoggedIn(true);
    } catch (error) {
        logger.error('Detailed Google login error:', {
            message: error.message,
            stack: error.stack,
            response: error.response
        });
        setError(error.message);
    }
  };

  const _handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

      // Add this console log to verify the client ID is loaded
    logger.info("Google Client ID:", process.env.REACT_APP_GOOGLE_CLIENT_ID);
    logger.info("Region:", process.env.REACT_APP_REGION);
    logger.info("Node Environment:", process.env.NODE_ENV);
    
    // Debug: Log all React environment variables
    const reactEnvVars = Object.keys(process.env)
      .filter(key => key.startsWith('REACT_APP_'))
      .reduce((obj, key) => {
        obj[key] = process.env[key];
        return obj;
      }, {});
    logger.info("All React environment variables:", reactEnvVars);
    
    // Alert for debugging in production
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      console.error("❌ REACT_APP_GOOGLE_CLIENT_ID is not defined!");
    }

  useEffect(() => {
    // Skip verbose env logging on results pages if quiet mode is enabled
    const isResultsFlow =
      location.pathname.startsWith('/math/') ||
      /^\/essay\/[^/]+\/results$/.test(location.pathname);
    if (QUIET_RESULTS_LOGS && isResultsFlow) return;
    logger.info('Environment variables:', {
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      apiBaseUrl: process.env.REACT_APP_API_BASE_URL
    });
  }, [location.pathname]);

  const [savedEssayData, setSavedEssayData] = useState(null);

  const handleContinueEssay = (essayData) => {
    logger.info('Continuing essay with data:', essayData);
    setSavedEssayData(essayData);
    setQuestion(essayData.question || '');
    setAnswer(essayData.answer || '');
    setCategory(essayData.category || 'Shakespeare');
    if (essayData.skipGrading && essayData.result) {
      setResult(essayData.result);
      setShortTaskId(essayData.task_id); 
    }
    if (essayData.essayId) {
      // setEssayId(essayData.essayId); // If you add essayId state to App.js
    }
    if (essayData.startAtStep) {
      setStep(essayData.startAtStep);
    } else {
      setStep(4); // Default to confirm answer step
    }
    // Navigate to main app page if not already there
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  // Update localStorage when shortTaskId changes
  useEffect(() => {
    if (shortTaskId) {
      try {
        localStorage.setItem('currentShortTaskId', shortTaskId);
      } catch (e) {
        logger.error('Failed to save to localStorage:', e);
      }
    }
  }, [shortTaskId]);

  const [dbTaskId, setDbTaskId] = useState(null);
  const [essayId, setEssayId] = useState(null);

  // Track if we've loaded from localStorage for the current route to prevent re-loading
  const loadedFromLocalStorageRef = React.useRef(false);
  const currentRouteRef = React.useRef(location.pathname);

  // Load continueEssay from localStorage when navigating to math or essay routes
  useEffect(() => {
    // Check if we're on a math or essay results route
    const isMathRoute = location.pathname.startsWith('/math/');
    const isEssayResultsRoute = location.pathname.match(/^\/essay\/[^/]+\/results$/);
    
    // Reset the ref if we've navigated to a different route
    if (currentRouteRef.current !== location.pathname) {
      loadedFromLocalStorageRef.current = false;
      currentRouteRef.current = location.pathname;
    }
    
    // Only load if we haven't already loaded for this route and we don't have savedEssayData
    if ((isMathRoute || isEssayResultsRoute) && !savedEssayData && !loadedFromLocalStorageRef.current) {
      const continueEssay = localStorage.getItem('continueEssay');
      if (continueEssay) {
        try {
          const essayData = JSON.parse(continueEssay);
          if (!QUIET_RESULTS_LOGS) {
            logger.info(`Loading continueEssay from localStorage for ${isMathRoute ? 'math' : 'essay'} route`);
          }
          setSavedEssayData(essayData);
          loadedFromLocalStorageRef.current = true;
        } catch (e) {
          logger.error('Error parsing continueEssay from localStorage:', e);
          localStorage.removeItem('continueEssay');
          loadedFromLocalStorageRef.current = true; // Mark as processed even on error
        }
      } else {
        // No data in localStorage, mark as processed
        loadedFromLocalStorageRef.current = true;
      }
    }
  }, [location.pathname, savedEssayData]);

  useEffect(() => {
    if (savedEssayData) {
      // Reduced logging - only log key info, not full object; skip if quiet and on results
      const isResultsFlow =
        location.pathname.startsWith('/math/') ||
        /^\/essay\/[^/]+\/results$/.test(location.pathname);
      if (!QUIET_RESULTS_LOGS || !isResultsFlow) {
        logger.info('Processing savedEssayData:', {
          category: savedEssayData.category,
          hasResult: !!savedEssayData.result,
          isMathFeedback: savedEssayData.isMathFeedback
        });
      }
      
      // Set basic data for all cases
      setQuestion(savedEssayData.question || '');
      setAnswer(savedEssayData.answer || '');
      setCategory(savedEssayData.category || 'Shakespeare');
      
      // For math feedback viewing, don't set question to avoid mode selection conflicts
      if (savedEssayData.isMathFeedback && savedEssayData.result) {
        // Don't set question for math feedback viewing
        setResult(savedEssayData.result);
        setShortTaskId(savedEssayData.task_id);
        setDbTaskId(savedEssayData.task_id);
        setEssayId(savedEssayData.essayId || null);
        setStep(2); // Go to math workflow step
        setIsViewingFeedback(true); // Mark that we're viewing feedback
        // Don't clear localStorage here - let MathWorkflow component handle it
        // This ensures MathWorkflow can read the data before it's cleared
      } else {
        // Normal handling for non-math or draft essays
        if (savedEssayData.skipGrading && savedEssayData.result) {
          logger.info('Setting result and preparing to show feedback view');
          setResult(savedEssayData.result);
          setShortTaskId(savedEssayData.task_id);
          setDbTaskId(savedEssayData.task_id);
          setEssayId(savedEssayData.essayId || null);
          
          // Check if this is math feedback
          if (savedEssayData.isMathFeedback) {
            logger.info('Directing to math workflow (step 2) for feedback');
            // For math feedback, go to step 2 which will render MathWorkflow
            // The category (MATH_Concepts or MATH_Applications) will determine the proper display
            setStep(2);
            setIsViewingFeedback(true); // Mark that we're viewing feedback
            // Don't clear localStorage here - let MathWorkflow component handle it
          } else {
            logger.info('Directing to regular feedback view (step 5)');
            // For regular feedback, continue to step 5 (ResultAndRevision)
            setStep(5);
            setIsViewingFeedback(true); // Mark that we're viewing feedback
            // Clear localStorage for non-math essays
            localStorage.removeItem('continueEssay');
          }
        } else if (savedEssayData.startAtStep) {
          setStep(savedEssayData.startAtStep);
          // Clear localStorage for draft essays
          localStorage.removeItem('continueEssay');
        }
      }
      
      // Clear the saved data after processing to prevent re-processing
      setSavedEssayData(null);
    }
  }, [savedEssayData]);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedFiliocht, setSelectedFiliocht] = useState(null);
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [selectedCraft, setSelectedCraft] = useState(null);
  const [_isCreatingComprehension, _setIsCreatingComprehension] = useState(false);
  const [_viewingSubmissionData, _setViewingSubmissionData] = useState(null);

  // Check if survey should be shown for students - temporarily disabled
  useEffect(() => {
    // Survey functionality temporarily disabled
    // const checkSurveyStatus = async () => {
    //   if (isAuthenticated && userData && userData.role === 'student') {
    //     try {
    //       const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
    //         method: 'GET',
    //         credentials: 'include',
    //         headers: {
    //           'Authorization': `Bearer ${authToken}`,
    //           'Content-Type': 'application/json'
    //         }
    //       });
    //       
    //       if (response.ok) {
    //         const profileData = await response.json();
    //         const isCompleted = profileData.survey_completed || false;
    //         setSurveyCompleted(isCompleted);
    //         
    //         // Show survey banner if it's not completed and user has been registered for more than 1 minute
    //         // This prevents showing it immediately on registration
    //         if (!isCompleted && profileData.created_at) {
    //           const createdTime = new Date(profileData.created_at);
    //           const now = new Date();
    //           const timeDiff = now - createdTime;
    //           const oneMinute = 60 * 1000;
    //           
    //           if (timeDiff > oneMinute) {
    //             // Check if banner was dismissed recently (don't show again for 24 hours after dismissal)
    //             const dismissed = localStorage.getItem('surveyBannerDismissed');
    //             const dismissedTime = localStorage.getItem('surveyBannerDismissedTime');
    //             const now = Date.now();
    //             const twentyFourHours = 24 * 60 * 60 * 1000;
    //             
    //             if (!dismissed || (dismissedTime && (now - parseInt(dismissedTime)) > twentyFourHours)) {
    //               setShowSurveyBanner(true);
    //             }
    //           }
    //         }
    //       }
    //     } catch (error) {
    //       logger.error('Error checking survey status:', error);
    //     }
    //   }
    // };

    // checkSurveyStatus();
  }, [isAuthenticated, userData, authToken]);

  // Determine if the user is from the UK
  const isUKUser = userData?.country === 'UK';
  logger.debug('AppContent - isUKUser:', isUKUser, 'based on country:', userData?.country);

  const handleViewQuizAttempt = (quizId, attemptId, quizSubject) => {
    logger.info(`Navigating to view quiz attempt: quizId=${quizId}, attemptId=${attemptId}, subject=${quizSubject}`);
    setCurrentQuizId(quizId);
    setCurrentAttemptId(attemptId);
    // The subject for the quiz workflow should be 'Biology' or 'Chemistry'
    // We need to ensure quizSubject is correctly passed or derived.
    // For now, we'll rely on it being passed if available, or derive it.
    const workflowSubject = getQuizSubjectFromCategory(quizSubject) || quizSubject; 
    setCategory(workflowSubject); 
    setStep(1); // This will trigger QuizWorkflow via MainContent
    navigate('/'); // Navigate to the main page where MainContent renders QuizWorkflow
  };

  // Define getCategorySubject at this level if not already available globally
  // or import it if it's in a utils file.
  // For now, let's assume it's similar to the one in Dashboard.js for simplicity
  const getQuizSubjectFromCategory = (cat) => {
    if (!cat) return null;
    
    // Handle all quiz subjects
    if (cat === 'Biology' || cat === 'Chemistry' || cat === 'Physics' ||
        cat === 'Business' || cat === 'Business Studies' ||
        cat === 'Legal Studies' || cat === 'LegalStudies' ||
        cat === 'Psychology') return cat;
    
    // Handle variants with prefixes/suffixes
    if (cat.startsWith('Biology_') || cat.startsWith('Chemistry_') || 
        cat.startsWith('Physics_') || cat.startsWith('Business_') ||
        cat.startsWith('LegalStudies_') || cat.startsWith('Psychology_')) {
      // Extract base subject name
      return cat.split('_')[0];
    }
    
    logger.warn(`Quiz subject mapping not found for category: ${cat}`);
    return null; // Fallback or more specific handling
  };

  // Function to clear quiz state
  const clearQuizState = () => {
    setCurrentQuizId(null);
    setCurrentAttemptId(null);
  };

  // Survey handlers
  const _handleSurveyComplete = () => {
    _setShowSurvey(false);
    _setShowSurveyBanner(false);
    _setSurveyCompleted(true);
    // Clear the dismissal flag since they completed it
    localStorage.removeItem('surveyBannerDismissed');
    localStorage.removeItem('surveyBannerDismissedTime');
    logger.info('Survey completed by user');
  };

  const _handleSurveySkip = () => {
    _setShowSurvey(false);
    logger.info('Survey skipped by user');
  };

  const _handleSurveyBannerDismiss = () => {
    _setShowSurveyBanner(false);
    localStorage.setItem('surveyBannerDismissed', 'true');
    localStorage.setItem('surveyBannerDismissedTime', Date.now().toString());
    logger.info('Survey banner dismissed by user');
  };

  const _handleSurveyBannerClick = () => {
    _setShowSurveyBanner(false);
    _setShowSurvey(true);
    logger.info('Survey opened from banner');
  };

  // Handle saving an essay as a draft
  const handleSaveEssay = async () => {
    try {
      const subject = getCategorySubject(category);
      
      const response = await fetch(`${API_BASE_URL}/api/essays`, {
        method: 'POST',
        credentials: 'include', 
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question,
          answer,
          category,
          subject,
          visible_to_teacher: shareWithTeacher
        })
      });

      if (response.ok) {
        // Track essay save
        track('essay_saved_draft', { 
          category: category,
          subject: subject,
          wordCount: answer ? answer.split(/\s+/).filter(word => word.length > 0).length : 0
        });
        alert('Essay saved successfully!\n\nYou can find all your saved essays in the Dashboard.');
      } else {
        alert('Failed to save essay. Please try again.');
      }
    } catch (error) {
      logger.error('Error saving essay:', error);
      alert(`Failed to save essay: ${error.message}`);
    }
  };

  return (
    <>
      {/* Cookie Consent Banner (controls marketing_consent localStorage) */}
      <CookieConsentBanner />
      {showOnboarding && (
        <OnboardingModal
          isOpen={showOnboarding}
          onClose={completeOnboarding}
          onActionSelect={handleOnboardingAction}
          slides={isTeacher ? TEACHER_ONBOARDING_SLIDES : undefined}
        />
      )}
      {isLoading && <LoadingAnecdotes isUKUser={isUKUser} />}
      {isAuthenticated && <SideNavbar onResetApp={handleResetApp} />}
      <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/register" element={<ErrorBoundary><MultiStepRegister /></ErrorBoundary>} />
        <Route path="/register-old" element={<ErrorBoundary><Register /></ErrorBoundary>} />
        <Route path="/verify-email/:token" element={<ErrorBoundary><VerifyEmail /></ErrorBoundary>} />
        <Route path="/forgot-password" element={<ErrorBoundary><ForgotPassword /></ErrorBoundary>} />
        <Route path="/reset-password/:token" element={<ErrorBoundary><ResetPassword /></ErrorBoundary>} />
        <Route path="/privacy" element={<ErrorBoundary><PrivacyPolicy /></ErrorBoundary>} />
        <Route path="/work-with-us" element={<ErrorBoundary><WorkWithUs /></ErrorBoundary>} />
        <Route path="/faq" element={<ErrorBoundary><RegionalFAQ /></ErrorBoundary>} />
        <Route path="/help" element={<ErrorBoundary><Help /></ErrorBoundary>} />
        <Route path="/help/join-classroom" element={<ErrorBoundary><HowToJoinClassroom /></ErrorBoundary>} />
        <Route path="/help/share-essay" element={<ErrorBoundary><HowToShareEssay /></ErrorBoundary>} />
        <Route path="/math-debug" element={<ErrorBoundary><TestMathRendering /></ErrorBoundary>} />
        {process.env.NODE_ENV !== 'production' && (
          <Route path="/dev/math-feedback-test" element={<ErrorBoundary><DevMathFeedbackPage /></ErrorBoundary>} />
        )}
        <Route path="/uk/faq" element={<ErrorBoundary><FAQ_UK /></ErrorBoundary>} />
        <Route path="/dashboard" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <Dashboard 
                  authToken={authToken} 
                  onContinueEssay={handleContinueEssay}
                  onViewQuizAttempt={handleViewQuizAttempt}
                />
              </div>
            </ErrorBoundary>
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route path="/my-submissions" element={
          isAuthenticated ? (
            userData?.role === 'teacher' ? (
              <ErrorBoundary>
                <div className="app-container">
                  <Header onResetApp={handleResetApp} />
                  <MySubmissions 
                    authToken={authToken} 
                    onContinueEssay={handleContinueEssay}
                    onViewQuizAttempt={handleViewQuizAttempt}
                  />
                </div>
              </ErrorBoundary>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route path="/my-classrooms" element={
          isAuthenticated && userData?.role === 'student' ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <MyClassrooms />
              </div>
            </ErrorBoundary>
          ) : (
            <Navigate to="/" />
          )
        } />
        <Route path="/comprehension-submissions" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <StudentSubmissionsList />
              </div>
            </ErrorBoundary>
          ) : (
            <Navigate to="/login" />
          )
        } />
        
        {/* Public Exam Papers Preview Routes - No authentication required */}
        <Route path="/exam-papers/preview" element={
          <ErrorBoundary>
            <PublicExamPapersList />
          </ErrorBoundary>
        } />
        <Route path="/exam-papers/preview/:subject" element={
          <ErrorBoundary>
            <PublicExamPaperPreview />
          </ErrorBoundary>
        } />
        <Route path="/exam-papers/preview/:subject/:year" element={
          <ErrorBoundary>
            <PublicExamPaperPreview />
          </ErrorBoundary>
        } />
        <Route path="/exam-papers/preview/:subject/:year/:paper" element={
          <ErrorBoundary>
            <PublicExamPaperPreview />
          </ErrorBoundary>
        } />
        
        {/* Exam Papers Subject Selection */}
        <Route path="/exam-papers/subject" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <ExamPapersSubjectSelection userData={userData} />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        {/* Topic Browser */}
        <Route path="/exam-papers/search-by-topic" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                {React.createElement(require('./components/exam-papers/TopicBrowser').default)}
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Exam Papers Routes - English HL via generic */}
        <Route path="/exam-papers/english-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/english-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/english-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/english-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Irish HL Papers Routes - generic */}
        <Route path="/exam-papers/irish-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/irish-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/irish-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/irish-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Mathematics HL Papers Routes - generic */}
        <Route path="/exam-papers/mathematics-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/mathematics-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/mathematics-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/mathematics-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/student-submissions" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <StudentSubmissions 
                  authToken={authToken} 
                  onContinueEssay={handleContinueEssay}
                />
              </div>
            </ErrorBoundary>
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route 
          path="/admin/feedback" 
          element={
            isAuthenticated && (username === "charles" || username === "cillian") ? 
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <AdminFeedback authToken={authToken} />
              </div>
            </ErrorBoundary> : 
            <Navigate to="/" />
          } 
        />
        <Route 
          path="/admin/essays" 
          element={
            isAuthenticated && (username === "charles" || username === "cillian") ? 
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <AdminEssays 
                  authToken={authToken}
                  onContinueEssay={handleContinueEssay}
                />
              </div>
            </ErrorBoundary>: 
            <Navigate to="/" />
          } 
        />
        <Route path="/about" element={<ErrorBoundary><About /></ErrorBoundary>} />
        <Route path="/uk/about" element={<ErrorBoundary><AboutUK /></ErrorBoundary>} />
        <Route 
            path="/feedback" 
            element={
              isAuthenticated ? (
                <ErrorBoundary>
                  <div className="app-container">
                    <Header onResetApp={handleResetApp} />
                    <Feedback />
                  </div>
                </ErrorBoundary>
              ) : (
                <Navigate to="/" />
              )
            } 
          />
        <Route 
            path="/account" 
            element={
              isAuthenticated ? (
                <ErrorBoundary>
                  <div className="app-container">
                    <Header onResetApp={handleResetApp} />
                    <MyAccount />
                  </div>
                </ErrorBoundary>
              ) : (
                <Navigate to="/" />
              )
            } 
          />
        <Route 
            path="/teacher-questions" 
            element={
              isAuthenticated && userData?.role === 'teacher' ? (
                <ErrorBoundary>
                  <div className="app-container">
                    <Header onResetApp={handleResetApp} />
                    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                      <TeacherQuestions userData={userData} />
                    </div>
                  </div>
                </ErrorBoundary>
              ) : (
                <Navigate to="/" />
              )
            } 
          />
        <Route 
            path="/classroom-management" 
            element={
              isAuthenticated && userData?.role === 'teacher' ? (
                <ErrorBoundary>
                  <ClassroomManagementPage />
                </ErrorBoundary>
              ) : (
                <Navigate to="/" />
              )
            } 
          />
        <Route
            path="/subscribe"
            element={
              isAuthenticated ? (
                <ErrorBoundary><Subscription /></ErrorBoundary>
              ) : (
                <Navigate to="/" />
              )
            }
          />
        <Route path="/winback" element={<ErrorBoundary><Winback /></ErrorBoundary>} />
        <Route path="/login" element={
          <ErrorBoundary>
            <LoginPage
              handleLogin={handleLogin}
              error={error}
              setError={setError}
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              showResendVerification={showResendVerification}
              handleResendVerification={handleResendVerification}
              storeToken={storeToken}
              storeUserData={storeUserData}
              navigate={navigate}
            />
          </ErrorBoundary>
        } />
        
        {/* UK-specific landing page route - accessed via Vercel rewrites for pulc.co.uk domain */}
        <Route path="/uk" element={<ErrorBoundary><RegionalLandingPage /></ErrorBoundary>} />
        
        {/* NEW URL-BASED ROUTES - using existing components */}
        
        {/* Essay workflow routes */}
        <Route path="/essay/subject" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <CategorySelectionNew
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                selectedFiliocht={selectedFiliocht}
                setSelectedFiliocht={setSelectedFiliocht}
                selectedPoem={selectedPoem}
                setSelectedPoem={setSelectedPoem}
                selectedCraft={selectedCraft}
                setSelectedCraft={setSelectedCraft}
                setCategory={setCategory}
                setQuestion={setQuestion}
                setStep={setStep}
                usageRemaining={usageRemaining}
                userCountry={userData?.country}
                userData={userData}
                onBackToHome={handleResetApp}
                selectedActivity="essay-feedback"
                // URL navigation functions instead of setStep
                onCategorySelected={(category, urlFriendlyCategory) => {
                  setCategory(category);
                  // Use custom URL format if provided, otherwise use category
                  const urlCategory = urlFriendlyCategory || (category ? category.toLowerCase().replace(/_/g, '-') : 'shakespeare');
                  navigate(`/essay/${urlCategory}/question`);
                }}
                onMathModeSelected={(category, mode) => {
                  setCategory(category);
                  setQuestion(mode);
                  navigate(`/math/${category.toLowerCase()}`);
                }}
                onQuizSubjectSelected={(subject) => {
                  setCategory(subject);
                  const slug = subject
                    .replace(/^AL_/, '')
                    .replace(/^GCSE_/, '')
                    .replace(/_/g, ' ')
                    .replace(/\s+/g, '-')
                    .toLowerCase();
                  const qs = subject.startsWith('AL_') ? '?curriculum=AL' : (subject.startsWith('GCSE_') ? '?curriculum=GCSE' : '');
                  navigate(`/quiz/${slug}${qs}`);
                }}
                onNotesSelected={() => {
                  navigate('/notes');
                }}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Essay question selection */}
        <Route path="/essay/:subject/question" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <QuestionSelectNew
                category={(() => {
                  if (category) return category;
                  // Convert URL-friendly format back to category format
                  const urlSubject = location.pathname.split('/')[2];
                  if (!urlSubject) return 'Shakespeare'; // Default fallback
                  if (urlSubject === 'english-hsc-crafta') {
                    return 'English_HSC_CraftA';
                  } else if (urlSubject === 'english-hsc-craftb') {
                    return 'English_HSC_CraftB';
                  }
                  // For other categories, convert dashes to underscores and capitalize
                  return urlSubject.replace(/-/g, '_');
                })()}
                question={question}
                setQuestion={setQuestion}
                handlePrevStep={() => {
                  const urlSubject = location.pathname.split('/')[2];
                  // If it's a craft category, go back to craft selection, otherwise go to subject selection
                  if (urlSubject === 'english-hsc-crafta' || urlSubject === 'english-hsc-craftb') {
                    // Set the state to show craft selection and navigate to subject page
                    setSelectedBranch('English');
                    setSelectedCraft('HSC_Craft_Selection');
                    navigate('/essay/subject');
                  } else {
                    navigate('/essay/subject');
                  }
                }}
                handleQuestionSubmit={() => {
                  // Navigate to upload step - we'll need to get subject from URL
                  const pathParts = location.pathname.split('/');
                  const subject = pathParts[2];
                  navigate(`/essay/${subject}/upload`);
                }}
                selectedPoem={selectedPoem}
                API_BASE_URL={API_BASE_URL}
                authToken={authToken}
                userData={userData}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Essay upload step */}
        <Route path="/essay/:subject/upload" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <UploadStepNew
                category={category}
                imageFiles={imageFiles}
                handleSelectImages={(event) => {
                  const newFiles = Array.from(event.target.files);
                  setImageFiles(prev => [...prev, ...newFiles]);
                }}
                moveImageUp={(index) => {
                  setImageFiles(prev => {
                    const newFiles = [...prev];
                    if (index > 0) {
                      [newFiles[index], newFiles[index - 1]] = [newFiles[index - 1], newFiles[index]];
                    }
                    return newFiles;
                  });
                }}
                moveImageDown={(index) => {
                  setImageFiles(prev => {
                    const newFiles = [...prev];
                    if (index < newFiles.length - 1) {
                      [newFiles[index], newFiles[index + 1]] = [newFiles[index + 1], newFiles[index]];
                    }
                    return newFiles;
                  });
                }}
                removeLocalImage={(index) => {
                  setImageFiles(prev => prev.filter((_, i) => i !== index));
                  // Also remove rotation for this index and shift others
                  setImageRotations(prev => {
                    const updated = { ...prev };
                    delete updated[index];
                    // Shift rotations down for indices after the removed one
                    Object.keys(updated).forEach(key => {
                      const k = parseInt(key);
                      if (k > index) {
                        updated[k - 1] = updated[k];
                        delete updated[k];
                      }
                    });
                    return updated;
                  });
                }}
                imageRotations={imageRotations}
                rotateImage={rotateImage}
                handleSubmitAnswer={handleSubmitAnswer}
                handlePrevStep={() => {
                  const pathParts = location.pathname.split('/');
                  const subject = pathParts[2];
                  navigate(`/essay/${subject}/question`);
                }}
                nextStep={() => {
                  const pathParts = location.pathname.split('/');
                  const subject = pathParts[2];
                  navigate(`/essay/${subject}/confirm`);
                }}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Essay confirm step */}
        <Route path="/essay/:subject/confirm" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <ConfirmAnswerStepNew
                category={category}
                wordCount={answer ? answer.split(/\s+/).filter(word => word.length > 0).length : 0}
                grammarWarning={grammarWarning}
                spellCheckEnabled={spellCheckEnabled}
                setSpellCheckEnabled={setSpellCheckEnabled}
                ignoreFadaEnabled={ignoreFadaEnabled}
                setIgnoreFadaEnabled={setIgnoreFadaEnabled}
                shareWithTeacher={shareWithTeacher}
                setShareWithTeacher={setShareWithTeacher}
                grammarExemption={grammarExemption}
                setGrammarExemption={setGrammarExemption}
                setUserChangedExemption={setUserChangedExemption}
                isSpellChecking={isSpellChecking}
                isLoading={isLoading}
                grammarCheckInProgress={grammarCheckInProgress}
                handleConfirmClick={async (feedbackType) => {
                  await handleConfirmAnswer(feedbackType, shareWithTeacher, ignoreFadaEnabled, grammarExemption, getCategorySubject(category), pendingTeacherReview);
                  // Navigation is handled inside handleConfirmAnswer based on pending_teacher_review
                  if (!pendingTeacherReview) {
                    const pathParts = location.pathname.split('/');
                    const subject = pathParts[2];
                    navigate(`/essay/${subject}/results`);
                  } else {
                    // Navigate to pending confirmation
                    navigate('/essay/pending-review');
                  }
                }}
                handleSaveEssay={handleSaveEssay}
                handlePrevStep={() => {
                  const pathParts = location.pathname.split('/');
                  const subject = pathParts[2];
                  navigate(`/essay/${subject}/upload`);
                }}
                renderAnswerInput={() => (
                  <IrishSpellcheckEditor
                    value={answer}
                    onChange={(v) => setAnswer(v)}
                    category={category}
                    spellCheckEnabled={spellCheckEnabled}
                    ignoreFadaEnabled={ignoreFadaEnabled}
                  />
                )}
                userData={userData}
                usageRemaining={usageRemaining}
                pendingTeacherReview={pendingTeacherReview}
                setPendingTeacherReview={setPendingTeacherReview}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Essay pending review confirmation */}
        <Route path="/essay/pending-review" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <PendingReviewConfirmation
                handleResetApp={handleResetApp}
                category={category}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Essay results */}
        <Route path="/essay/:subject/results" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              {result ? (
                <ResultAndRevision
                  question={question}
                  result={result}
                  usageRemaining={usageRemaining}
                  feedbackType="inline"
                  answer={answer}
                  setAnswer={setAnswer}
                  handleResetApp={handleResetApp}
                  handleSavePDF={handleSavePDF}
                  isRevising={isRevising}
                  setIsRevising={setIsRevising}
                  setStep={setStep}
                  editedSections={editedSections}
                  setEditedSections={setEditedSections}
                  scrollToTop={() => window.scrollTo(0, 0)}
                  category={category}
                  taskId={dbTaskId || shortTaskId}
                  authToken={authToken}
                  API_BASE_URL={API_BASE_URL}
                  essayId={essayId}
                  userData={userData}
                  teacherFeedback={null}
                  initialTeacherFeedback={null}
                  wordCount={answer ? answer.split(/\s+/).filter(word => word.length > 0).length : 0}
                />
              ) : (
                <div className="text-center p-8">
                  <p>No results available. Please submit an essay first.</p>
                  <button
                    onClick={() => navigate('/essay/subject')}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
                  >
                    Start New Essay
                  </button>
                </div>
              )}
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Math workflow */}
        <Route path="/math" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <CategorySelectionNew
                selectedBranch="Maths"
                setSelectedBranch={setSelectedBranch}
                selectedFiliocht={selectedFiliocht}
                setSelectedFiliocht={setSelectedFiliocht}
                selectedPoem={selectedPoem}
                setSelectedPoem={setSelectedPoem}
                selectedCraft={selectedCraft}
                setSelectedCraft={setSelectedCraft}
                setCategory={setCategory}
                setQuestion={setQuestion}
                setStep={setStep}
                usageRemaining={usageRemaining}
                userCountry={userData?.country}
                userData={userData}
                onBackToHome={handleResetApp}
                selectedActivity="math-help"
                onMathModeSelected={(category, mode) => {
                  setCategory(category);
                  setQuestion(mode);
                  navigate(`/math/${category.toLowerCase()}`);
                }}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Math category specific routes */}
        <Route path="/math/math_concepts" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <MainContent
                step={2}
                setStep={setStep}
                category="MATH_Concepts"
                setCategory={setCategory}
                question={question}
                setQuestion={setQuestion}
                usageRemaining={usageRemaining}
                setUsageRemaining={setUsageRemaining}
                handleQuestionSubmit={handleQuestionSubmit}
                prevStep={prevStep}
                nextStep={nextStep}
                imageFiles={imageFiles}
                handleImageUpload={handleImageUpload}
                handleRemoveImage={handleRemoveImage}
                answer={answer}
                setAnswer={setAnswer}
                handleAnswerChange={handleAnswerChange}
                result={result}
                setResult={setResult}
                handleResetApp={handleResetApp}
                feedbackSubmitted={feedbackSubmitted}
                handleFeedbackSubmit={handleFeedbackSubmit}
                feedbackCategory={feedbackCategory}
                setFeedbackCategory={setFeedbackCategory}
                feedbackText={feedbackText}
                setFeedbackText={setFeedbackText}
                handleSavePDF={handleSavePDF}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                authToken={authToken}
                handleImageFiles={handleImageFiles}
                API_BASE_URL={API_BASE_URL}
                onConfirmAnswer={handleConfirmAnswer}
                userData={userData}
                savedEssayData={savedEssayData}
                clearSavedEssayData={() => setSavedEssayData(null)}
                shortTaskId={shortTaskId}
                setShortTaskId={setShortTaskId}
                dbTaskId={dbTaskId}
                essayId={essayId}
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                currentQuizId={currentQuizId}
                currentAttemptId={currentAttemptId}
                clearQuizState={clearQuizState}
                // New props for shared state
                isRevising={isRevising}
                setIsRevising={setIsRevising}
                editedSections={editedSections}
                setEditedSections={setEditedSections}
                spellCheckEnabled={spellCheckEnabled}
                setSpellCheckEnabled={setSpellCheckEnabled}
                grammarWarning={grammarWarning}
                setGrammarWarning={setGrammarWarning}
                grammarCheckInProgress={grammarCheckInProgress}
                setGrammarCheckInProgress={setGrammarCheckInProgress}
                isSpellChecking={isSpellChecking}
                setIsSpellChecking={setIsSpellChecking}
                shareWithTeacher={shareWithTeacher}
                setShareWithTeacher={setShareWithTeacher}
                pendingTeacherReview={pendingTeacherReview}
                setPendingTeacherReview={setPendingTeacherReview}
                ignoreFadaEnabled={ignoreFadaEnabled}
                setIgnoreFadaEnabled={setIgnoreFadaEnabled}
                grammarExemption={grammarExemption}
                setGrammarExemption={setGrammarExemption}
                userChangedExemption={userChangedExemption}
                setUserChangedExemption={setUserChangedExemption}
                handleSaveEssay={handleSaveEssay}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        <Route path="/math/math_applications" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <MainContent
                  step={2}
                  setStep={setStep}
                  category="MATH_Applications"
                  setCategory={setCategory}
                  question={question}
                  setQuestion={setQuestion}
                  usageRemaining={usageRemaining}
                  setUsageRemaining={setUsageRemaining}
                  handleQuestionSubmit={handleQuestionSubmit}
                  prevStep={prevStep}
                  nextStep={nextStep}
                  imageFiles={imageFiles}
                  handleImageUpload={handleImageUpload}
                  handleRemoveImage={handleRemoveImage}
                  answer={answer}
                  setAnswer={setAnswer}
                  handleAnswerChange={handleAnswerChange}
                  result={result}
                  setResult={setResult}
                  handleResetApp={handleResetApp}
                  feedbackSubmitted={feedbackSubmitted}
                  handleFeedbackSubmit={handleFeedbackSubmit}
                  feedbackCategory={feedbackCategory}
                  setFeedbackCategory={setFeedbackCategory}
                  feedbackText={feedbackText}
                  setFeedbackText={setFeedbackText}
                  handleSavePDF={handleSavePDF}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  authToken={authToken}
                  handleImageFiles={handleImageFiles}
                  API_BASE_URL={API_BASE_URL}
                  onConfirmAnswer={handleConfirmAnswer}
                  userData={userData}
                  savedEssayData={savedEssayData}
                  clearSavedEssayData={() => setSavedEssayData(null)}
                  shortTaskId={shortTaskId}
                  setShortTaskId={setShortTaskId}
                  dbTaskId={dbTaskId}
                  essayId={essayId}
                  selectedBranch={selectedBranch}
                  setSelectedBranch={setSelectedBranch}
                  currentQuizId={currentQuizId}
                  currentAttemptId={currentAttemptId}
                  clearQuizState={clearQuizState}
                  // Add missing props
                  isRevising={isRevising}
                  setIsRevising={setIsRevising}
                  editedSections={editedSections}
                  setEditedSections={setEditedSections}
                  spellCheckEnabled={spellCheckEnabled}
                  setSpellCheckEnabled={setSpellCheckEnabled}
                  grammarWarning={grammarWarning}
                  setGrammarWarning={setGrammarWarning}
                  grammarCheckInProgress={grammarCheckInProgress}
                  setGrammarCheckInProgress={setGrammarCheckInProgress}
                  isSpellChecking={isSpellChecking}
                  setIsSpellChecking={setIsSpellChecking}
                  shareWithTeacher={shareWithTeacher}
                  setShareWithTeacher={setShareWithTeacher}
                  pendingTeacherReview={pendingTeacherReview}
                  setPendingTeacherReview={setPendingTeacherReview}
                  ignoreFadaEnabled={ignoreFadaEnabled}
                  setIgnoreFadaEnabled={setIgnoreFadaEnabled}
                  grammarExemption={grammarExemption}
                  setGrammarExemption={setGrammarExemption}
                  userChangedExemption={userChangedExemption}
                  setUserChangedExemption={setUserChangedExemption}
                  handleSaveEssay={handleSaveEssay}
                />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Quiz workflow */}
        <Route path="/quiz" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <CategorySelectionNew
                selectedBranch={null}
                setSelectedBranch={setSelectedBranch}
                selectedFiliocht={selectedFiliocht}
                setSelectedFiliocht={setSelectedFiliocht}
                selectedPoem={selectedPoem}
                setSelectedPoem={setSelectedPoem}
                selectedCraft={selectedCraft}
                setSelectedCraft={setSelectedCraft}
                setCategory={setCategory}
                setQuestion={setQuestion}
                setStep={setStep}
                usageRemaining={usageRemaining}
                userCountry={userData?.country}
                userData={userData}
                onBackToHome={handleResetApp}
                selectedActivity="practice-quizzes"
                onQuizSubjectSelected={(subject) => {
                  setCategory(subject);
                  const slug = subject
                    .replace(/^AL_/, '')
                    .replace(/^GCSE_/, '')
                    .replace(/_/g, ' ')
                    .replace(/\s+/g, '-')
                    .toLowerCase();
                  const qs = subject.startsWith('AL_') ? '?curriculum=AL' : (subject.startsWith('GCSE_') ? '?curriculum=GCSE' : '');
                  navigate(`/quiz/${slug}${qs}`);
                }}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Quiz subject specific routes */}
        <Route path="/quiz/biology" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <QuizSubjectRoute
                API_BASE_URL={API_BASE_URL}
                authToken={authToken}
                userData={userData}
                onBack={() => navigate('/quiz')}
                currentQuizId={currentQuizId}
                currentAttemptId={currentAttemptId}
                setCurrentQuizId={setCurrentQuizId}
                setCurrentAttemptId={setCurrentAttemptId}
                subjectOverride="Biology"
              />
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        <Route path="/quiz/chemistry" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="Chemistry"
            />
          ) : <Navigate to="/login" />
        } />

        <Route path="/quiz/business" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="Business"
            />
          ) : <Navigate to="/login" />
        } />

        <Route path="/quiz/physics" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="Physics"
            />
          ) : <Navigate to="/login" />
        } />
        <Route path="/quiz/geography" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="Geography"
            />
          ) : <Navigate to="/login" />
        } />
        <Route path="/quiz/history" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="History"
            />
          ) : <Navigate to="/login" />
        } />
        <Route path="/quiz/legal-studies" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="Legal Studies"
            />
          ) : <Navigate to="/login" />
        } />

        <Route path="/quiz/legalstudies" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="LegalStudies"
            />
          ) : <Navigate to="/login" />
        } />

        <Route path="/quiz/business-studies" element={
          isAuthenticated ? (
            <QuizSubjectRoute
              API_BASE_URL={API_BASE_URL}
              authToken={authToken}
              userData={userData}
              onBack={() => navigate('/quiz')}
              currentQuizId={currentQuizId}
              currentAttemptId={currentAttemptId}
              setCurrentQuizId={setCurrentQuizId}
              setCurrentAttemptId={setCurrentAttemptId}
              subjectOverride="Business Studies"
            />
          ) : <Navigate to="/login" />
        } />

        <Route path="/quiz/psychology" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <QuizSubjectRoute
                API_BASE_URL={API_BASE_URL}
                authToken={authToken}
                userData={userData}
                onBack={() => navigate('/quiz')}
                currentQuizId={currentQuizId}
                currentAttemptId={currentAttemptId}
                setCurrentQuizId={setCurrentQuizId}
                setCurrentAttemptId={setCurrentAttemptId}
                subjectOverride="Psychology"
              />
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        <Route path="/quiz/economics" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <QuizSubjectRoute
                API_BASE_URL={API_BASE_URL}
                authToken={authToken}
                userData={userData}
                onBack={() => navigate('/quiz')}
                currentQuizId={currentQuizId}
                currentAttemptId={currentAttemptId}
                setCurrentQuizId={setCurrentQuizId}
                setCurrentAttemptId={setCurrentAttemptId}
                subjectOverride="Economics"
              />
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Catch-all route for any subject not explicitly defined above */}
        <Route path="/quiz/:subject" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <QuizSubjectRoute
                API_BASE_URL={API_BASE_URL}
                authToken={authToken}
                userData={userData}
                onBack={() => navigate('/quiz')}
                currentQuizId={currentQuizId}
                currentAttemptId={currentAttemptId}
                setCurrentQuizId={setCurrentQuizId}
                setCurrentAttemptId={setCurrentAttemptId}
              />
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        

        {/* Comprehension workflow */}
        <Route path="/comprehension" element={
          isAuthenticated ? (
            <div className="app-container">
              <Header onResetApp={handleResetApp} />
              <ComprehensionView 
                API_BASE_URL={API_BASE_URL} 
                authToken={authToken} 
                userData={userData} 
                onSubmissionComplete={(submissionData) => {
                  console.log('Comprehension submission completed:', submissionData);
                }}
              />
            </div>
          ) : <Navigate to="/login" />
        } />

        {/* Notes workflow */}
        <Route path="/notes" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                {userData?.role === 'teacher' ? (
                  <TeacherDocumentManager
                    API_BASE_URL={API_BASE_URL}
                    authToken={authToken}
                    userData={userData}
                    onBack={handleResetApp}
                    onQuizCreated={(quizId, subject) => {
                      setCurrentQuizId(quizId);
                      const workflowSubject = subject || 'Biology';
                      setCategory(workflowSubject);
                      setStep(1);
                      navigate('/');
                    }}
                  />
                ) : (
                  <StudentNotesWorkflow
                    API_BASE_URL={API_BASE_URL}
                    authToken={authToken}
                    userData={userData}
                    onBack={handleResetApp}
                  />
                )}
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        
        {/* Flashcard workflow */}
        <Route path="/flashcards" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <FlashcardWorkflow
                  API_BASE_URL={API_BASE_URL}
                  authToken={authToken}
                  userData={userData}
                  onBack={handleResetApp}
                />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        
        <Route path="/" element={
          isAuthenticated ? (
            (userData?.role === 'teacher' && !isViewingFeedback && !savedEssayData && currentQuizId === null) ? (
              <TeacherHomepage />  // Show teacher-specific homepage for teachers (unless viewing feedback)
            ) : (
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                
                {/* Survey Banner - temporarily hidden */}
                {/* {showSurveyBanner && userData && userData.role === 'student' && (
                  <SurveyBanner
                    onDismiss={handleSurveyBannerDismiss}
                    onComplete={handleSurveyBannerClick}
                  />
                )} */}

                <MainContent
                  step={Math.floor(step)}
                  setStep={setStep}
                  category={category}
                  setCategory={setCategory}
                  question={question}
                  setQuestion={setQuestion}
                  usageRemaining={usageRemaining}
                  setUsageRemaining={setUsageRemaining}
                  handleQuestionSubmit={handleQuestionSubmit}
                  prevStep={prevStep}
                  nextStep={nextStep}
                  imageFiles={imageFiles}
                  handleImageUpload={handleImageUpload}
                  handleRemoveImage={handleRemoveImage}
                  answer={answer}
                  setAnswer={setAnswer}
                  handleAnswerChange={handleAnswerChange}
                  result={result}
                  setResult={setResult}
                  handleResetApp={handleResetApp}
                  feedbackSubmitted={feedbackSubmitted}
                  handleFeedbackSubmit={handleFeedbackSubmit}
                  feedbackCategory={feedbackCategory}
                  setFeedbackCategory={setFeedbackCategory}
                  feedbackText={feedbackText}
                  setFeedbackText={setFeedbackText}
                  handleSavePDF={handleSavePDF}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  authToken={authToken}
                  handleImageFiles={handleImageFiles}
                  API_BASE_URL={API_BASE_URL}
                  onConfirmAnswer={handleConfirmAnswer}
                  userData={userData}
                  savedEssayData={savedEssayData}
                  clearSavedEssayData={() => setSavedEssayData(null)}
                  shortTaskId={shortTaskId}
                  setShortTaskId={setShortTaskId}
                  dbTaskId={dbTaskId}
                  essayId={essayId}
                  selectedBranch={selectedBranch}
                  setSelectedBranch={setSelectedBranch}
                  currentQuizId={currentQuizId}
                  currentAttemptId={currentAttemptId}
                  clearQuizState={clearQuizState}
                  // Add missing props for grammar functionality
                  isRevising={isRevising}
                  setIsRevising={setIsRevising}
                  editedSections={editedSections}
                  setEditedSections={setEditedSections}
                  spellCheckEnabled={spellCheckEnabled}
                  setSpellCheckEnabled={setSpellCheckEnabled}
                  grammarWarning={grammarWarning}
                  setGrammarWarning={setGrammarWarning}
                  grammarCheckInProgress={grammarCheckInProgress}
                  setGrammarCheckInProgress={setGrammarCheckInProgress}
                  isSpellChecking={isSpellChecking}
                  setIsSpellChecking={setIsSpellChecking}
                  shareWithTeacher={shareWithTeacher}
                  setShareWithTeacher={setShareWithTeacher}
                  pendingTeacherReview={pendingTeacherReview}
                  setPendingTeacherReview={setPendingTeacherReview}
                  ignoreFadaEnabled={ignoreFadaEnabled}
                  setIgnoreFadaEnabled={setIgnoreFadaEnabled}
                  grammarExemption={grammarExemption}
                  setGrammarExemption={setGrammarExemption}
                  userChangedExemption={userChangedExemption}
                  setUserChangedExemption={setUserChangedExemption}
                  handleSaveEssay={handleSaveEssay}
                />
              </div>
            )
          ) : (
            <RegionalLandingPage />  // Show region-appropriate landing page for non-authenticated users
          )
        } />
        {/* Add route for student's submission list */} 
        <Route 
          path="/my-submissions" 
          element={authToken ? <ErrorBoundary><StudentSubmissionsList /></ErrorBoundary> : <Navigate to="/login" />} 
        />
        {/* Add route for viewing a specific submission detail */} 
        <Route 
          path="/submission/:submissionId" 
          element={authToken ? <ErrorBoundary><ComprehensionResults API_BASE_URL={API_BASE_URL} authToken={authToken} userData={userData} /></ErrorBoundary> : <Navigate to="/login" />} 
        />
        {/* Add route for teacher comprehensions management */}
        <Route 
          path="/teacher/comprehensions" 
          element={authToken ? <ErrorBoundary><ComprehensionResults API_BASE_URL={API_BASE_URL} authToken={authToken} userData={userData} /></ErrorBoundary> : <Navigate to="/login" />} 
        />
        <Route path="/referral-terms" element={<ErrorBoundary><ReferralTerms /></ErrorBoundary>} />
        <Route path="/ie-tandcs" element={<ErrorBoundary><IeTandCs /></ErrorBoundary>} />
        <Route path="/uk-tandcs" element={<ErrorBoundary><UkTandCs /></ErrorBoundary>} />
        <Route path="/au-tandcs" element={<ErrorBoundary><AuTandCs /></ErrorBoundary>} />
        <Route path="/trustworthy-ai" element={<ErrorBoundary><TrustworthyAI /></ErrorBoundary>} />
        <Route path="/test-design-system" element={<ErrorBoundary><TestNewDesignSystem /></ErrorBoundary>} />
        <Route path="/notifications" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <NotificationCenter />
              </div>
            </ErrorBoundary>
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route 
          path="/todos" 
          element={
            isAuthenticated && (username === "charles" || username === "cillian") ? 
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <TodoList />
              </div>
            </ErrorBoundary> : 
            <Navigate to="/" />
          } 
        />
        <Route path="/essay/:id" element={isAuthenticated ? <ErrorBoundary><EssayLoader onContinueEssay={handleContinueEssay} /></ErrorBoundary> : <Navigate to="/login" />} />
        <Route path="/study-plans" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <StudyPlans />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Physics HL Papers Routes */}
        <Route path="/exam-papers/physics-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="physics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/physics-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="physics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/physics-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="physics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/physics-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="physics-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Spanish HL Papers Routes */}
        <Route path="/exam-papers/spanish-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="spanish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/spanish-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="spanish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/spanish-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="spanish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/spanish-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="spanish-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* English OL Papers Routes */}
        <Route path="/exam-papers/english-ol" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/english-ol/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/english-ol/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/english-ol/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="english-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Irish OL Papers Routes */}
        <Route path="/exam-papers/irish-ol" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/irish-ol/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/irish-ol/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/irish-ol/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="irish-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Mathematics OL Papers Routes */}
        <Route path="/exam-papers/mathematics-ol" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/mathematics-ol/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/mathematics-ol/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/mathematics-ol/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="mathematics-ol" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        <Route path="/student-submissions" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <StudentSubmissions 
                  authToken={authToken} 
                  onContinueEssay={handleContinueEssay}
                />
              </div>
            </ErrorBoundary>
          ) : (
            <Navigate to="/login" />
          )
        } />

        {/* Art HL Papers Routes */}
        <Route path="/exam-papers/art-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="art-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/art-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="art-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/art-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="art-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/art-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="art-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Biology HL Papers Routes */}
        <Route path="/exam-papers/biology-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="biology-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/biology-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="biology-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/biology-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="biology-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/biology-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="biology-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Business HL Papers Routes */}
        <Route path="/exam-papers/business-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="business-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/business-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="business-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/business-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="business-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/business-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="business-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Chemistry HL Papers Routes */}
        <Route path="/exam-papers/chemistry-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="chemistry-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/chemistry-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="chemistry-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/chemistry-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="chemistry-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/chemistry-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="chemistry-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Construction HL Papers Routes */}
        <Route path="/exam-papers/construction-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="construction-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/construction-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="construction-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/construction-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="construction-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/construction-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="construction-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* French HL Papers Routes */}
        <Route path="/exam-papers/french-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="french-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/french-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="french-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/french-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="french-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/french-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="french-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Geography HL Papers Routes */}
        <Route path="/exam-papers/geography-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="geography-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/geography-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="geography-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/geography-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="geography-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/geography-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="geography-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* German HL Papers Routes */}
        <Route path="/exam-papers/german-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="german-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/german-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="german-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/german-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="german-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/german-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="german-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* History HL Papers Routes */}
        <Route path="/exam-papers/history-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="history-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/history-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="history-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/history-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="history-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/history-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="history-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />

        {/* Home Economics HL Papers Routes */}
        <Route path="/exam-papers/home-ec-hl" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="home-ec-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/home-ec-hl/:year" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="home-ec-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/home-ec-hl/:year/:paper" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="home-ec-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/exam-papers/home-ec-hl/:year/:paper/:section" element={
          isAuthenticated ? (
            <ErrorBoundary>
              <div className="app-container">
                <Header onResetApp={handleResetApp} />
                <GenericExamPapers authToken={authToken} userData={userData} subjectId="home-ec-hl" />
              </div>
            </ErrorBoundary>
          ) : <Navigate to="/login" />
        } />
        <Route path="/teacher/classroom-setup" element={<ErrorBoundary><TeacherClassroomSetup /></ErrorBoundary>} />
      </Routes>
      </Suspense>
      <Analytics />
      
      {/* Survey Modal - temporarily hidden */}
      {/* {showSurvey && userData && userData.role === 'student' && (
        <EntranceSurvey
          user={userData}
          authToken={authToken}
          onComplete={handleSurveyComplete}
          onSkip={handleSurveySkip}
        />
      )} */}
      
      {/* Rate Limit Modal */}
      {rateLimitData && (
        <RateLimitModal
          isOpen={showRateLimitModal}
          onClose={() => setShowRateLimitModal(false)}
          feature={rateLimitData.feature}
          usage={rateLimitData.usage}
          nextReset={rateLimitData.nextReset}
          userData={userData}
          onSubmitToTeacher={async () => {
            setPendingTeacherReview(true);
            setShowRateLimitModal(false);
            
            const subject = getCategorySubject(category);
            const isRouteBased = location.pathname.includes('/essay/');
            
            try {
              await handleConfirmAnswer('inline', false, ignoreFadaEnabled, grammarExemption, subject, true);
              
              if (isRouteBased) {
                navigate('/essay/pending-review');
              }
            } catch (error) {
              logger.error('Error submitting to teacher:', error);
              setIsLoading(false);
            }
          }}
        />
      )}
    </>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AppContent />
    </GoogleOAuthProvider>
  );
}

export default App;

